import React from 'react';
import actions from 'actions';
import core from 'core';
import ToggleZoomOverlay from 'components/ToggleZoomOverlay';
import TrackChangeOverlay from 'components/TrackChangeOverlay';
import ToolsOverlay from 'components/ToolsOverlay';
import Ribbons from 'components/Ribbons';
import ApplyFormFieldButton from 'components/ApplyFormFieldButton';
import DataElements from 'constants/dataElement';
import defaultTool from 'constants/defaultTool';
import { defaultZoomList } from 'constants/zoomFactors';
import { copyMapWithDataProperties } from 'constants/map';
import { defaultNoteDateFormat, defaultPrintedNoteDateFormat } from 'constants/defaultTimeFormat';
import getHashParameters from 'helpers/getHashParameters';
import localStorageManager from 'helpers/localStorageManager';
import { undoButton, redoButton } from 'helpers/commonToolbarElements';
import defaultFonts from 'constants/defaultFonts';
import webFonts from 'constants/webFonts';
import isContentEditWarningHidden from 'helpers/isContentEditWarningHidden';
import presetCropDimensions from 'constants/presetCropDimensions';
import presetNewPageDimensions from 'constants/presetNewPageDimensions';
import defaultDateTimeFormats from 'constants/defaultDateTimeFormats';
import { redactionTypeMap } from 'constants/redactionTypes';
import { getMeasurementScalePreset, initialScale } from 'constants/measurementScale';
import { availableFontFaces, cssFontValues } from 'constants/officeEditorFonts';
import { OfficeEditorEditMode } from 'constants/officeEditor';
import SignatureModes from 'constants/signatureModes';
import { ShortcutKeys } from 'helpers/hotkeysManager';
import defaultToolsWithInlineComment from 'src/constants/defaultToolsWithInlineCommentOnAnnotationSelected';
import { SYNC_MODES } from 'constants/multiViewerContants';
import { getInstanceID } from 'helpers/getRootNode';
import { initialColors, initialTextColors } from 'helpers/initialColorStates';
import { defaultModularComponents, defaultModularHeaders, defaultFlyoutMap, defaultPanels } from './modularComponents';
import { PANEL_SIZES } from 'constants/panel';

const { ToolNames } = window.Core.Tools;
const instanceId = getInstanceID();

export default {
  viewer: {
    initalsOffset: 0,
    isInitialsModeEnabled: false,
    isMultiViewerMode: false,
    isMultiViewerReady: false,
    multiViewerSyncScrollMode: SYNC_MODES.SKIP_UNMATCHED,
    syncViewer: null,
    isCompareStarted: false,
    isComparisonOverlayEnabled: true,
    compareAnnotationsMap: {},
    activeDocumentViewerKey: 1,
    zoomLevels: {
      1: 1,
      2: 1,
    },
    canUndo: {
      1: false,
      2: false,
    },
    canRedo: {
      1: false,
      2: false,
    },
    TabManager: null,
    tabs: [],
    activeTab: 0,
    isMultiTab: false,
    tabNameHandler: null,
    thumbnailSelectingPages: false,
    isInDesktopOnlyMode: false,
    toolbarGroup: DataElements.ANNOTATE_TOOLBAR_GROUP,
    activeTheme: 'light',
    currentLanguage: 'en',
    disabledElements: {
      [DataElements.MULTI_VIEWER_SAVE_DOCUMENT_BUTTON]: { disabled: true, priority: 2 },
      [DataElements.SAVED_SIGNATURES_TAB]: { disabled: true, priorty: 2 },
      [DataElements.CALIBRATION_POPUP_BUTTON]: { disabled: true, priorty: 2 },
      [DataElements.LEGACY_RICH_TEXT_POPUP]: { disabled: true, priority: 2 },
      [DataElements.LOGO_BAR]: { disabled: true, priority: 2 },
      'comparePanelToggle': { disabled: true, priority: 2 },
    },
    enabledRibbonsStash: [],
    selectedScale: initialScale,
    isAddingNewScale: false,
    calibrationInfo: {
      isCalibration: false,
      tempScale: '',
      previousToolName: '',
      isFractionalUnit: false,
      defaultUnit: '',
    },
    deleteScale: '',
    openElements: {
      header: true,
      toolsHeader: true,
      [DataElements.STYLE_POPUP_TEXT_STYLE_CONTAINER]: true,
      [DataElements.STYLE_POPUP_LABEL_TEXT_CONTAINER]: true,
      [DataElements.FORM_FIELD_INDICATOR_CONTAINER]: true,
      [DataElements.CUSTOM_MODAL]: true,
      [DataElements.RICH_TEXT_STYLE_CONTAINER]: true,
      [DataElements.FORMULA_BAR]: true,
    },
    hiddenElements: {},
    panelWidths: {
      leftPanel: 264,
      searchPanel: 293,
      notesPanel: 293,
      indexPanel: 293,
      redactionPanel: 330,
      textEditingPanel: 330,
      wv3dPropertiesPanel: 307,
      comparePanel: 330,
      watermarkPanel: 330,
      stylePanel: 330,
      signatureListPanel: 330,
      rubberStampPanel: 330,
      customLeftPanel: 330,
      formFieldPanel: 307,
      tabPanel: 330,
      officeEditorReviewPanel: 330,
    },
    mobilePanelSize: PANEL_SIZES.SMALL_SIZE,
    documentContainerWidth: null,
    documentContainerHeight: null,
    lastPickedToolForGroup: {},
    lastPickedToolGroup: {},
    lastActiveToolForRibbon: {},
    highContrastMode: getHashParameters('highContrastMode', false),
    notesInLeftPanel: getHashParameters('notesInLeftPanel', false),
    autoFocusNoteOnAnnotationSelection: getHashParameters('autoFocusNoteOnAnnotationSelection', true),
    fadePageNavigationComponent: true,
    pageDeletionConfirmationModalEnabled: true,
    autoExpandOutlines: getHashParameters('autoExpandOutlines', false),
    isAnnotationNumberingEnabled: getHashParameters('enableAnnotationNumbering', false),
    bookmarkIconShortcutVisibility: false,
    hideContentEditWarning: isContentEditWarningHidden(),
    contentEditWorkersLoaded: false,
    isContentEditingEnabled: false,
    currentContentBeingEdited: null,
    pageManipulationOverlayAlternativePosition: null,
    pageManipulationOverlayOpenByRightClick: true,
    headers: {
      default: [
        {
          type: 'toggleElementButton',
          dataElement: DataElements.MENU_OVERLAY_BUTTON,
          element: DataElements.MENU_OVERLAY,
          img: 'ic-hamburger-menu',
          title: 'component.menuOverlay',
        },
        {
          type: 'divider',
          hidden: ['small-mobile'],
        },
        {
          type: 'toggleElementButton',
          img: 'icon-header-sidebar-line',
          element: 'leftPanel',
          dataElement: DataElements.LEFT_PANEL_BUTTON,
          title: 'component.leftPanel',
        },
        {
          type: 'toggleElementButton',
          img: 'icon-header-page-manipulation-line',
          element: DataElements.VIEW_CONTROLS_OVERLAY,
          dataElement: DataElements.VIEW_CONTROLS_OVERLAY_BUTTON,
          title: 'component.viewControls',
        },
        {
          type: 'divider',
          hidden: ['small-mobile'],
        },
        {
          type: 'customElement',
          render: () => <ToggleZoomOverlay />,
          dataElement: DataElements.ZOOM_OVERLAY_BUTTON,
          element: DataElements.ZOOM_OVERLAY,
          hiddenOnMobileDevice: true,
        },
        { type: 'divider', hidden: ['small-mobile', 'mobile', 'tablet'] },
        { type: 'toolButton', toolName: 'Pan' },
        { type: 'toolButton', toolName: 'TextSelect' }, // For mobile
        { type: 'toolButton', toolName: 'AnnotationEdit', hidden: ['small-mobile', 'mobile'] },
        {
          type: 'customElement',
          render: () => <Ribbons />,
          className: 'custom-ribbons-container',
        },
        {
          type: 'customElement',
          render: () => <TrackChangeOverlay />,
          dataElement: DataElements.TRACK_CHANGE_OVERLAY_BUTTON,
          isOfficeEditorOnly: true,
        },
        {
          type: 'divider',
          hidden: ['small-mobile'],
          isOfficeEditorOnly: true,
        },
        {
          type: 'toggleElementButton',
          dataElement: 'searchButton',
          element: 'searchPanel',
          img: 'icon-header-search',
          title: 'component.searchPanel',
          hidden: ['small-mobile'],
        },
        {
          type: 'toggleElementButton',
          dataElement: 'toggleNotesButton',
          img: 'icon-header-chat-line',
          title: 'component.notesPanel',
          element: 'notesPanel',
          hidden: ['small-mobile'],
        },
        {
          type: 'actionButton',
          dataElement: 'moreButton',
          title: 'action.more',
          img: 'icon-tools-more',
          onClick: dispatch => {
            dispatch(actions.setActiveHeaderGroup('small-mobile-more-buttons'));
            core.setToolMode(defaultTool);
          },
          hidden: ['mobile', 'tablet', 'desktop'],
        },
      ],
      'small-mobile-more-buttons': [
        {
          type: 'toggleElementButton',
          dataElement: 'searchButton',
          element: 'searchPanel',
          img: 'icon-header-search',
          title: 'component.searchPanel',
        },
        {
          type: 'toggleElementButton',
          dataElement: 'toggleNotesButton',
          element: 'notesPanel',
          img: 'icon-header-chat-line',
          title: 'component.notesPanel',
        },
        { type: 'spacer' },
        {
          type: 'actionButton',
          dataElement: 'defaultHeaderButton',
          titile: 'action.close',
          img: 'ic_close_black_24px',
          onClick: dispatch => {
            dispatch(actions.setActiveHeaderGroup('default'));
            core.setToolMode(defaultTool);
          },
        },
      ],
      [DataElements.VIEW_TOOLBAR_GROUP]: [],
      [DataElements.ANNOTATE_TOOLBAR_GROUP]: [
        { type: 'spacer' },
        {
          type: 'toolGroupButton',
          toolGroup: 'underlineTools',
          dataElement: 'underlineToolGroupButton',
          title: 'annotation.underline',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'highlightTools',
          dataElement: 'highlightToolGroupButton',
          title: 'annotation.highlight',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'rectangleTools',
          dataElement: 'shapeToolGroupButton',
          title: 'annotation.rectangle',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'freeTextTools',
          dataElement: 'freeTextToolGroupButton',
          title: 'annotation.freetext',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'freeHandHighlightTools',
          dataElement: 'freeHandHighlightToolGroupButton',
          title: 'annotation.freeHandHighlight',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'freeHandTools',
          dataElement: 'freeHandToolGroupButton',
          title: 'annotation.freehand',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'stickyTools',
          dataElement: 'stickyToolGroupButton',
          title: 'annotation.stickyNote',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'squigglyTools',
          dataElement: 'squigglyToolGroupButton',
          title: 'annotation.squiggly',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'strikeoutTools',
          dataElement: 'strikeoutToolGroupButton',
          title: 'annotation.strikeout',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'markInsertTextTools',
          dataElement: 'markInsertTextGroupButton',
          title: 'annotation.markInsertText',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'markReplaceTextTools',
          dataElement: 'markReplaceTextGroupButton',
          title: 'annotation.markReplaceText',
        },
        { type: 'divider' },
        {
          type: 'customElement',
          render: () => <ToolsOverlay />,
          dataElement: 'toolsOverlay',
          hidden: ['small-mobile', 'mobile'],
        },
        {
          ...undoButton,
          hideOnClick: true,
        },
        {
          ...redoButton,
          hideOnClick: true,
        },
        { type: 'toolButton', toolName: 'AnnotationEraserTool' },
        { type: 'spacer', hidden: ['tablet', 'mobile', 'small-mobile'] },
      ],
      [DataElements.SHAPES_TOOLBAR_GROUP]: [
        { type: 'spacer' },
        {
          type: 'toolGroupButton',
          toolGroup: 'rectangleTools',
          dataElement: 'shapeToolGroupButton',
          title: 'annotation.rectangle',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'freeHandHighlightTools',
          dataElement: 'freeHandHighlightToolGroupButton',
          title: 'annotation.freeHandHighlight',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'freeHandTools',
          dataElement: 'freeHandToolGroupButton',
          title: 'annotation.freehand',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'lineTools',
          dataElement: 'lineToolGroupButton',
          title: 'annotation.line',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'polyLineTools',
          dataElement: 'polyLineToolGroupButton',
          title: 'annotation.polyline',
        },

        {
          type: 'toolGroupButton',
          toolGroup: 'arrowTools',
          dataElement: 'arrowToolGroupButton',
          title: 'annotation.arrow',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'arcTools',
          dataElement: 'arcToolGroupButton',
          title: 'annotation.arc',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'ellipseTools',
          dataElement: 'ellipseToolGroupButton',
          title: 'annotation.ellipse',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'polygonTools',
          dataElement: 'polygonToolGroupButton',
          title: 'annotation.polygon',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'cloudTools',
          dataElement: 'polygonCloudToolGroupButton',
          title: 'annotation.polygonCloud',
        },

        { type: 'divider' },
        {
          type: 'customElement',
          render: () => <ToolsOverlay />,
          dataElement: 'toolsOverlay',
          hidden: ['small-mobile', 'mobile'],
        },
        undoButton,
        redoButton,
        { type: 'toolButton', toolName: 'AnnotationEraserTool' },
        { type: 'spacer', hidden: ['tablet', 'mobile', 'small-mobile'] },
      ],
      [DataElements.REDACT_TOOLBAR_GROUP]: [
        { type: 'spacer' },
        {
          type: 'toolGroupButton',
          toolGroup: 'redactionTools',
          dataElement: 'redactionToolGroupButton',
          title: 'annotation.redact',
        },
        {
          type: 'actionButton',
          toolGroup: 'pageRedactionTools',
          dataElement: 'pageRedactionToolGroupButton',
          title: 'action.redactPages',
          showColor: 'never',
          img: 'icon-tool-page-redact',
          onClick: dispatch => dispatch(actions.openElement(DataElements.PAGE_REDACT_MODAL)),
        },
        { type: 'divider' },
        {
          type: 'customElement',
          render: () => <ToolsOverlay />,
          dataElement: 'toolsOverlay',
          hidden: ['small-mobile', 'mobile'],
        },
        {
          type: 'toggleElementButton',
          img: 'icon-redact-panel',
          element: DataElements.REDACTION_PANEL,
          dataElement: DataElements.REDACTION_PANEL_TOGGLE,
          title: 'component.redactionPanel',
        },
        undoButton,
        redoButton,
        { type: 'toolButton', toolName: 'AnnotationEraserTool' },
        { type: 'spacer', hidden: ['tablet', 'mobile', 'small-mobile'] },
      ],
      [DataElements.INSERT_TOOLBAR_GROUP]: [
        { type: 'spacer' },
        {
          type: 'toolGroupButton',
          toolGroup: 'rubberStampTools',
          img: 'icon-tool-stamp-line',
          dataElement: 'rubberStampToolGroupButton',
          title: 'annotation.rubberStamp',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'signatureTools',
          img: 'icon-tool-signature',
          dataElement: 'signatureToolGroupButton',
          title: 'annotation.signature',
          showColor: 'never',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'stampTools',
          img: 'icon-tool-image-line',
          dataElement: 'stampToolGroupButton',
          title: 'annotation.image',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'fileAttachmentTools',
          img: 'ic_fileattachment_24px',
          dataElement: 'fileAttachmentToolGroupButton',
          title: 'annotation.fileattachment',
          showColor: 'never',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'calloutTools',
          dataElement: 'calloutToolGroupButton',
          title: 'annotation.callout',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'model3DTools',
          img: 'icon-tool-model3d',
          dataElement: 'threeDToolGroupButton',
          title: 'annotation.3D',
          showColor: 'never',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'changeViewTools',
          dataElement: 'changeViewToolGroupButton',
          title: 'annotation.changeView',
        },
        { type: 'divider' },
        {
          type: 'customElement',
          render: () => <ToolsOverlay />,
          dataElement: 'toolsOverlay',
          hidden: ['small-mobile', 'mobile'],
        },
        undoButton,
        redoButton,
        { type: 'toolButton', toolName: 'AnnotationEraserTool' },
        { type: 'spacer', hidden: ['tablet', 'mobile', 'small-mobile'] },
      ],
      [DataElements.MEASURE_TOOLBAR_GROUP]: [
        { type: 'spacer' },
        // @todo: add "measurement" to data-element names in 9.0. Note: do not rename arcMeasurementTools to arcTools as it is being used elsewhere.
        {
          type: 'toolGroupButton',
          toolGroup: 'distanceTools',
          dataElement: 'distanceToolGroupButton',
          title: 'annotation.distanceMeasurement',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'arcMeasurementTools',
          dataElement: 'arcMeasurementToolGroupButton',
          title: 'annotation.arcMeasurement',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'perimeterTools',
          dataElement: 'perimeterToolGroupButton',
          title: 'annotation.perimeterMeasurement',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'areaTools',
          dataElement: 'areaToolGroupButton',
          title: 'annotation.areaMeasurement',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'ellipseAreaTools',
          dataElement: 'ellipseAreaToolGroupButton',
          title: 'annotation.ellipseMeasurement',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'rectangleAreaTools',
          dataElement: 'rectangleAreaToolGroupButton',
          title: 'annotation.rectangularAreaMeasurement',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'cloudyRectangleAreaTools',
          dataElement: 'cloudyRectangleAreaToolGroupButton',
          title: 'annotation.areaMeasurement',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'countTools',
          dataElement: 'countToolGroupButton',
          title: 'annotation.countMeasurement',
        },
        { type: 'divider' },
        {
          type: 'customElement',
          render: () => <ToolsOverlay />,
          dataElement: 'toolsOverlay',
          hidden: ['small-mobile', 'mobile'],
        },
        undoButton,
        redoButton,
        { type: 'toolButton', toolName: 'AnnotationEraserTool' },
        { type: 'spacer', hidden: ['tablet', 'mobile', 'small-mobile'] },
      ],
      [DataElements.EDIT_TOOLBAR_GROUP]: [
        { type: 'spacer' },
        {
          type: 'toolGroupButton',
          toolGroup: 'cropTools',
          dataElement: 'cropToolGroupButton',
          title: 'annotation.crop',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'snippingTools',
          dataElement: 'snippingToolGroupButton',
          title: 'annotation.snipping',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'contentEditTools',
          dataElement: 'contentEditButton',
          title: 'action.edit',
        },
        {
          type: 'toggleElementButton',
          img: 'icon-watermark-panel',
          element: DataElements.WATERMARK_PANEL,
          dataElement: DataElements.WATERMARK_PANEL_TOGGLE,
          title: 'component.watermarkPanel',
        },
        { type: 'spacer', hidden: ['mobile', 'small-mobile'] },
      ],
      [DataElements.EDIT_TEXT_TOOLBAR_GROUP]: [
        { type: 'spacer' },
        {
          type: 'toolGroupButton',
          toolGroup: 'addParagraphTools',
          dataElement: 'addParagraphToolGroupButton',
          title: 'action.addParagraph',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'addImageContentTools',
          dataElement: 'addImageContentToolGroupButton',
          title: 'annotation.newImage',
        },
        { type: 'spacer', hidden: ['mobile', 'small-mobile'] },
      ],
      [DataElements.FILL_AND_SIGN_TOOLBAR_GROUP]: [
        { type: 'spacer' },
        {
          type: 'toolGroupButton',
          toolGroup: 'rubberStampTools',
          img: 'icon-tool-stamp-line',
          dataElement: 'rubberStampToolGroupButton',
          title: 'annotation.rubberStamp',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'freeTextTools',
          dataElement: 'freeTextToolGroupButton',
          title: 'annotation.freetext',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'signatureTools',
          img: 'icon-tool-signature',
          dataElement: 'signatureToolGroupButton',
          title: 'annotation.signature',
          showColor: 'never',
        },

        {
          type: 'toolGroupButton',
          toolGroup: 'crossStampTools',
          img: 'icon-tool-cross-stamp',
          dataElement: 'crossStampToolButton',
          title: 'annotation.formFillCross',
          showColor: 'never',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'checkStampTools',
          img: 'icon-tool-check-stamp',
          dataElement: 'checkStampToolButton',
          title: 'annotation.formFillCheckmark',
          showColor: 'never',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'dotStampTools',
          img: 'icon-tool-dot-stamp',
          dataElement: 'dotStampToolButton',
          title: 'annotation.formFillDot',
          showColor: 'never',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'dateFreeTextTools',
          dataElement: 'dateFreeTextToolButton',
          title: 'annotation.dateFreeText',
        },
        { type: 'divider' },
        {
          type: 'customElement',
          render: () => <ToolsOverlay />,
          dataElement: 'toolsOverlay',
          hidden: ['small-mobile', 'mobile'],
        },
        undoButton,
        redoButton,
        { type: 'toolButton', toolName: 'AnnotationEraserTool' },
        { type: 'spacer', hidden: ['tablet', 'mobile', 'small-mobile'] },
      ],
      [DataElements.FORMS_TOOLBAR_GROUP]: [
        { type: 'spacer' },
        {
          type: 'toolGroupButton',
          toolGroup: 'sigFieldTools',
          dataElement: 'signatureFieldToolGroupButton',
          title: 'annotation.signatureFormField',
          showColor: 'always',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'formFieldTools',
          dataElement: 'textFieldToolGroupButton',
          title: 'annotation.textField',
          showColor: 'always',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'freeTextTools',
          dataElement: 'freeTextToolGroupButton',
          title: 'annotation.freetext',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'checkBoxFieldTools',
          dataElement: 'checkBoxFieldToolGroupButton',
          title: 'annotation.checkBoxFormField',
          showColor: 'always',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'radioButtonFieldTools',
          dataElement: 'radioButtonFieldToolGroupButton',
          title: 'annotation.radioButtonFormField',
          showColor: 'always',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'listBoxFieldTools',
          dataElement: 'listBoxFieldToolGroupButton',
          title: 'annotation.listBoxFormField',
          showColor: 'always',
        },
        {
          type: 'toolGroupButton',
          toolGroup: 'comboBoxFieldTools',
          dataElement: 'comboBoxFieldToolGroupButton',
          title: 'annotation.comboBoxFormField',
          showColor: 'always',
        },
        { type: 'divider' },
        {
          type: 'customElement',
          render: () => <ToolsOverlay />,
          dataElement: 'toolsOverlay',
          hidden: ['small-mobile', 'mobile'],
        },
        {
          type: 'customElement',
          dataElement: 'applyFormFieldsButton',
          render: () => <ApplyFormFieldButton />,
        },
        { type: 'spacer', hidden: ['mobile', 'small-mobile'] },
      ],
    },
    customHeadersAdditionalProperties: {},
    enableRightClickAnnotationPopup: false,
    annotationPopup: [
      { dataElement: 'viewFileButton' },
      { dataElement: 'annotationCommentButton' },
      { dataElement: 'annotationStyleEditButton' },
      { dataElement: 'annotationDateEditButton' },
      { dataElement: 'annotationRedactButton' },
      { dataElement: 'annotationCropButton' },
      { dataElement: 'annotationContentEditButton' },
      { dataElement: 'annotationClearSignatureButton' },
      { dataElement: 'annotationGroupButton' },
      { dataElement: 'annotationUngroupButton' },
      { dataElement: 'formFieldEditButton' },
      { dataElement: DataElements.CALIBRATION_POPUP_BUTTON },
      { dataElement: 'linkButton' },
      { dataElement: 'fileAttachmentDownload' },
      { dataElement: 'annotationDeleteButton' },
      { dataElement: 'shortCutKeysFor3D' },
      { dataElement: 'playSoundButton' },
      { dataElement: 'openAlignmentButton' },
      { dataElement: 'issueAddButton' },
    ],
    textPopup: [
      { dataElement: 'copyTextButton' },
      { dataElement: 'textHighlightToolButton' },
      { dataElement: 'textUnderlineToolButton' },
      { dataElement: 'textSquigglyToolButton' },
      { dataElement: 'textStrikeoutToolButton' },
      { dataElement: 'textRedactToolButton' },
      { dataElement: 'linkButton' },
    ],
    contextMenuPopup: [
      { dataElement: 'panToolButton' },
      { dataElement: 'stickyToolButton' },
      { dataElement: 'highlightToolButton' },
      { dataElement: 'freeHandToolButton' },
      { dataElement: 'freeHandHighlightToolButton' },
      { dataElement: 'freeTextToolButton' },
      { dataElement: 'markInsertTextToolButton' },
      { dataElement: 'markReplaceTextToolButton' },
    ],
    menuOverlay: [
      { dataElement: 'newDocumentButton' },
      { dataElement: 'filePickerButton' },
      { dataElement: 'fullscreenButton' },
      { dataElement: 'downloadButton' },
      { dataElement: 'saveAsButton' },
      { dataElement: 'printButton' },
      { dataElement: 'themeChangeButton' },
      { dataElement: 'languageButton' },
    ],
    pageManipulationOverlay: [
      { dataElement: 'pageAdditionalControls' },
      { type: 'divider' },
      { dataElement: 'pageRotationControls' },
      { type: 'divider' },
      { dataElement: 'pageManipulationControls' },
    ],
    multiPageManipulationControls: [
      { dataElement: 'leftPanelPageTabsRotate' },
      { type: 'divider' },
      { dataElement: 'leftPanelPageTabsMove' },
      { type: 'divider' },
      { dataElement: 'leftPanelPageTabsMore' },
    ],
    multiPageManipulationControlsLarge: [
      { dataElement: 'leftPanelPageTabsRotate' },
      { type: 'divider' },
      { dataElement: 'leftPanelPageTabsMove' },
      { type: 'divider' },
      { dataElement: 'leftPanelPageTabsOperations' },
    ],
    multiPageManipulationControlsSmall: [
      { dataElement: 'leftPanelPageTabsRotate' },
      { type: 'divider' },
      { dataElement: 'leftPanelPageTabsMoreSmall' },
    ],
    thumbnailControlMenu: [{ dataElement: 'thumbRotateClockwise' }, { dataElement: 'thumbDelete' }],
    toolButtonObjects: {
      AnnotationCreateCountMeasurement: {
        dataElement: 'countMeasurementToolButton',
        title: 'annotation.countMeasurement',
        img: 'ic_check_black_24px',
        group: 'countTools',
        showColor: 'always',
      },
      AnnotationCreateCountMeasurement2: {
        dataElement: 'countMeasurementToolButton2',
        title: 'annotation.countMeasurement',
        img: 'ic_check_black_24px',
        group: 'countTools',
        showColor: 'always',
      },
      AnnotationCreateCountMeasurement3: {
        dataElement: 'countMeasurementToolButton3',
        title: 'annotation.countMeasurement',
        img: 'ic_check_black_24px',
        group: 'countTools',
        showColor: 'always',
      },
      AnnotationCreateCountMeasurement4: {
        dataElement: 'countMeasurementToolButton4',
        title: 'annotation.countMeasurement',
        img: 'ic_check_black_24px',
        group: 'countTools',
        showColor: 'always',
      },
      AnnotationCreateDistanceMeasurement: {
        dataElement: 'distanceMeasurementToolButton',
        title: 'annotation.distanceMeasurement',
        img: 'icon-tool-measurement-distance-line',
        group: 'distanceTools',
        showColor: 'always',
      },
      AnnotationCreateDistanceMeasurement2: {
        dataElement: 'distanceMeasurementToolButton2',
        title: 'annotation.distanceMeasurement',
        img: 'icon-tool-measurement-distance-line',
        group: 'distanceTools',
        showColor: 'always',
      },
      AnnotationCreateDistanceMeasurement3: {
        dataElement: 'distanceMeasurementToolButton3',
        title: 'annotation.distanceMeasurement',
        img: 'icon-tool-measurement-distance-line',
        group: 'distanceTools',
        showColor: 'always',
      },
      AnnotationCreateDistanceMeasurement4: {
        dataElement: 'distanceMeasurementToolButton4',
        title: 'annotation.distanceMeasurement',
        img: 'icon-tool-measurement-distance-line',
        group: 'distanceTools',
        showColor: 'always',
      },
      AnnotationCreatePerimeterMeasurement: {
        dataElement: 'perimeterMeasurementToolButton',
        title: 'annotation.perimeterMeasurement',
        img: 'icon-tool-measurement-perimeter',
        group: 'perimeterTools',
        showColor: 'always',
      },
      AnnotationCreatePerimeterMeasurement2: {
        dataElement: 'perimeterMeasurementToolButton2',
        title: 'annotation.perimeterMeasurement',
        img: 'icon-tool-measurement-perimeter',
        group: 'perimeterTools',
        showColor: 'always',
      },
      AnnotationCreatePerimeterMeasurement3: {
        dataElement: 'perimeterMeasurementToolButton3',
        title: 'annotation.perimeterMeasurement',
        img: 'icon-tool-measurement-perimeter',
        group: 'perimeterTools',
        showColor: 'always',
      },
      AnnotationCreatePerimeterMeasurement4: {
        dataElement: 'perimeterMeasurementToolButton4',
        title: 'annotation.perimeterMeasurement',
        img: 'icon-tool-measurement-perimeter',
        group: 'perimeterTools',
        showColor: 'always',
      },
      AnnotationCreateArcMeasurement: {
        dataElement: 'arcMeasurementToolButton',
        title: 'annotation.arcMeasurement',
        img: 'icon-tool-measurement-arc',
        group: 'arcMeasurementTools',
        showColor: 'always',
      },
      AnnotationCreateArcMeasurement2: {
        dataElement: 'arcMeasurementToolButton2',
        title: 'annotation.arcMeasurement',
        img: 'icon-tool-measurement-arc',
        group: 'arcMeasurementTools',
        showColor: 'always',
      },
      AnnotationCreateArcMeasurement3: {
        dataElement: 'arcMeasurementToolButton3',
        title: 'annotation.arcMeasurement',
        img: 'icon-tool-measurement-arc',
        group: 'arcMeasurementTools',
        showColor: 'always',
      },
      AnnotationCreateArcMeasurement4: {
        dataElement: 'arcMeasurementToolButton4',
        title: 'annotation.arcMeasurement',
        img: 'icon-tool-measurement-arc',
        group: 'arcMeasurementTools',
        showColor: 'always',
      },
      AnnotationCreateAreaMeasurement: {
        dataElement: 'areaMeasurementToolButton',
        title: 'annotation.areaMeasurement',
        img: 'icon-tool-measurement-area-polygon-line',
        group: 'areaTools',
        showColor: 'always',
      },
      AnnotationCreateAreaMeasurement2: {
        dataElement: 'areaMeasurementToolButton2',
        title: 'annotation.areaMeasurement',
        img: 'icon-tool-measurement-area-polygon-line',
        group: 'areaTools',
        showColor: 'always',
      },
      AnnotationCreateAreaMeasurement3: {
        dataElement: 'areaMeasurementToolButton3',
        title: 'annotation.areaMeasurement',
        img: 'icon-tool-measurement-area-polygon-line',
        group: 'areaTools',
        showColor: 'always',
      },
      AnnotationCreateAreaMeasurement4: {
        dataElement: 'areaMeasurementToolButton4',
        title: 'annotation.areaMeasurement',
        img: 'icon-tool-measurement-area-polygon-line',
        group: 'areaTools',
        showColor: 'always',
      },
      AnnotationCreateEllipseMeasurement: {
        dataElement: 'ellipseMeasurementToolButton',
        title: 'annotation.ellipseMeasurement',
        img: 'icon-tool-measurement-area-ellipse-line',
        group: 'ellipseAreaTools',
        showColor: 'always',
      },
      AnnotationCreateEllipseMeasurement2: {
        dataElement: 'ellipseMeasurementToolButton2',
        title: 'annotation.ellipseMeasurement',
        img: 'icon-tool-measurement-area-ellipse-line',
        group: 'ellipseAreaTools',
        showColor: 'always',
      },
      AnnotationCreateEllipseMeasurement3: {
        dataElement: 'ellipseMeasurementToolButton3',
        title: 'annotation.ellipseMeasurement',
        img: 'icon-tool-measurement-area-ellipse-line',
        group: 'ellipseAreaTools',
        showColor: 'always',
      },
      AnnotationCreateEllipseMeasurement4: {
        dataElement: 'ellipseMeasurementToolButton4',
        title: 'annotation.ellipseMeasurement',
        img: 'icon-tool-measurement-area-ellipse-line',
        group: 'ellipseAreaTools',
        showColor: 'always',
      },
      AnnotationCreateRectangularAreaMeasurement: {
        dataElement: 'rectangularAreaMeasurementToolButton',
        title: 'annotation.rectangularAreaMeasurement',
        img: 'icon-tool-measurement-area-line',
        group: 'rectangleAreaTools',
        showColor: 'always',
      },
      AnnotationCreateRectangularAreaMeasurement2: {
        dataElement: 'rectangularAreaMeasurementToolButton2',
        title: 'annotation.rectangularAreaMeasurement',
        img: 'icon-tool-measurement-area-line',
        group: 'rectangleAreaTools',
        showColor: 'always',
      },
      AnnotationCreateRectangularAreaMeasurement3: {
        dataElement: 'rectangularAreaMeasurementToolButton3',
        title: 'annotation.rectangularAreaMeasurement',
        img: 'icon-tool-measurement-area-line',
        group: 'rectangleAreaTools',
        showColor: 'always',
      },
      AnnotationCreateRectangularAreaMeasurement4: {
        dataElement: 'rectangularAreaMeasurementToolButton4',
        title: 'annotation.rectangularAreaMeasurement',
        img: 'icon-tool-measurement-area-line',
        group: 'rectangleAreaTools',
        showColor: 'always',
      },
      AnnotationCreateCloudyRectangularAreaMeasurement: {
        dataElement: 'cloudyRectangularAreaMeasurementToolButton',
        title: 'annotation.areaMeasurement',
        img: 'icon-tool-measurement-cloud-area-line',
        group: 'cloudyRectangleAreaTools',
        showColor: 'always',
      },
      AnnotationCreateCloudyRectangularAreaMeasurement2: {
        dataElement: 'cloudyRectangularAreaMeasurementToolButton2',
        title: 'annotation.areaMeasurement',
        img: 'icon-tool-measurement-cloud-area-line',
        group: 'cloudyRectangleAreaTools',
        showColor: 'always',
      },
      AnnotationCreateCloudyRectangularAreaMeasurement3: {
        dataElement: 'cloudyRectangularAreaMeasurementToolButton3',
        title: 'annotation.areaMeasurement',
        img: 'icon-tool-measurement-cloud-area-line',
        group: 'cloudyRectangleAreaTools',
        showColor: 'always',
      },
      AnnotationCreateCloudyRectangularAreaMeasurement4: {
        dataElement: 'cloudyRectangularAreaMeasurementToolButton4',
        title: 'annotation.areaMeasurement',
        img: 'icon-tool-measurement-cloud-area-line',
        group: 'cloudyRectangleAreaTools',
        showColor: 'always',
      },
      AnnotationCreateFreeHand: {
        dataElement: 'freeHandToolButton',
        title: 'annotation.freehand',
        img: 'icon-tool-pen-line',
        group: 'freeHandTools',
        showColor: 'always',
      },
      AnnotationCreateFreeHand2: {
        dataElement: 'freeHandToolButton2',
        title: 'annotation.freehand',
        img: 'icon-tool-pen-line',
        group: 'freeHandTools',
        showColor: 'always',
      },
      AnnotationCreateFreeHand3: {
        dataElement: 'freeHandToolButton3',
        title: 'annotation.freehand',
        img: 'icon-tool-pen-line',
        group: 'freeHandTools',
        showColor: 'always',
      },
      AnnotationCreateFreeHand4: {
        dataElement: 'freeHandToolButton4',
        title: 'annotation.freehand',
        img: 'icon-tool-pen-line',
        group: 'freeHandTools',
        showColor: 'always',
      },
      AnnotationCreateFreeHandHighlight: {
        dataElement: 'freeHandHighlightToolButton',
        title: 'annotation.freeHandHighlight',
        img: 'icon-tool-pen-highlight',
        group: 'freeHandHighlightTools',
        showColor: 'always',
      },
      AnnotationCreateFreeHandHighlight2: {
        dataElement: 'freeHandHighlightToolButton2',
        title: 'annotation.freeHandHighlight',
        img: 'icon-tool-pen-highlight',
        group: 'freeHandHighlightTools',
        showColor: 'always',
      },
      AnnotationCreateFreeHandHighlight3: {
        dataElement: 'freeHandHighlightToolButton3',
        title: 'annotation.freeHandHighlight',
        img: 'icon-tool-pen-highlight',
        group: 'freeHandHighlightTools',
        showColor: 'always',
      },
      AnnotationCreateFreeHandHighlight4: {
        dataElement: 'freeHandHighlightToolButton4',
        title: 'annotation.freeHandHighlight',
        img: 'icon-tool-pen-highlight',
        group: 'freeHandHighlightTools',
        showColor: 'always',
      },
      AnnotationCreateTextHighlight: {
        dataElement: 'highlightToolButton',
        title: 'annotation.highlight',
        img: 'icon-tool-text-manipulation-highlight',
        group: 'highlightTools',
        showColor: 'always',
      },
      AnnotationCreateTextHighlight2: {
        dataElement: 'highlightToolButton2',
        title: 'annotation.highlight',
        img: 'icon-tool-text-manipulation-highlight',
        group: 'highlightTools',
        showColor: 'always',
      },
      AnnotationCreateTextHighlight3: {
        dataElement: 'highlightToolButton3',
        title: 'annotation.highlight',
        img: 'icon-tool-text-manipulation-highlight',
        group: 'highlightTools',
        showColor: 'always',
      },
      AnnotationCreateTextHighlight4: {
        dataElement: 'highlightToolButton4',
        title: 'annotation.highlight',
        img: 'icon-tool-text-manipulation-highlight',
        group: 'highlightTools',
        showColor: 'always',
      },
      AnnotationCreateTextUnderline: {
        dataElement: 'underlineToolButton',
        title: 'annotation.underline',
        img: 'icon-tool-text-manipulation-underline',
        group: 'underlineTools',
        showColor: 'always',
      },
      AnnotationCreateTextUnderline2: {
        dataElement: 'underlineToolButton2',
        title: 'annotation.underline',
        img: 'icon-tool-text-manipulation-underline',
        group: 'underlineTools',
        showColor: 'always',
      },
      AnnotationCreateTextUnderline3: {
        dataElement: 'underlineToolButton3',
        title: 'annotation.underline',
        img: 'icon-tool-text-manipulation-underline',
        group: 'underlineTools',
        showColor: 'always',
      },
      AnnotationCreateTextUnderline4: {
        dataElement: 'underlineToolButton4',
        title: 'annotation.underline',
        img: 'icon-tool-text-manipulation-underline',
        group: 'underlineTools',
        showColor: 'always',
      },
      AnnotationCreateTextSquiggly: {
        dataElement: 'squigglyToolButton',
        title: 'annotation.squiggly',
        img: 'icon-tool-text-manipulation-squiggly',
        group: 'squigglyTools',
        showColor: 'always',
      },
      AnnotationCreateTextSquiggly2: {
        dataElement: 'squigglyToolButton2',
        title: 'annotation.squiggly',
        img: 'icon-tool-text-manipulation-squiggly',
        group: 'squigglyTools',
        showColor: 'always',
      },
      AnnotationCreateTextSquiggly3: {
        dataElement: 'squigglyToolButton3',
        title: 'annotation.squiggly',
        img: 'icon-tool-text-manipulation-squiggly',
        group: 'squigglyTools',
        showColor: 'always',
      },
      AnnotationCreateTextSquiggly4: {
        dataElement: 'squigglyToolButton4',
        title: 'annotation.squiggly',
        img: 'icon-tool-text-manipulation-squiggly',
        group: 'squigglyTools',
        showColor: 'always',
      },
      AnnotationCreateTextStrikeout: {
        dataElement: 'strikeoutToolButton',
        title: 'annotation.strikeout',
        img: 'icon-tool-text-manipulation-strikethrough',
        group: 'strikeoutTools',
        showColor: 'always',
      },
      AnnotationCreateTextStrikeout2: {
        dataElement: 'strikeoutToolButton2',
        title: 'annotation.strikeout',
        img: 'icon-tool-text-manipulation-strikethrough',
        group: 'strikeoutTools',
        showColor: 'always',
      },
      AnnotationCreateTextStrikeout3: {
        dataElement: 'strikeoutToolButton3',
        title: 'annotation.strikeout',
        img: 'icon-tool-text-manipulation-strikethrough',
        group: 'strikeoutTools',
        showColor: 'always',
      },
      AnnotationCreateTextStrikeout4: {
        dataElement: 'strikeoutToolButton4',
        title: 'annotation.strikeout',
        img: 'icon-tool-text-manipulation-strikethrough',
        group: 'strikeoutTools',
        showColor: 'always',
      },
      AnnotationCreateFreeText: {
        dataElement: 'freeTextToolButton',
        title: 'annotation.freetext',
        img: 'icon-tool-text-free-text',
        group: 'freeTextTools',
        showColor: 'always',
      },
      AnnotationCreateFreeText2: {
        dataElement: 'freeTextToolButton2',
        title: 'annotation.freetext',
        img: 'icon-tool-text-free-text',
        group: 'freeTextTools',
        showColor: 'always',
      },
      AnnotationCreateFreeText3: {
        dataElement: 'freeTextToolButton3',
        title: 'annotation.freetext',
        img: 'icon-tool-text-free-text',
        group: 'freeTextTools',
        showColor: 'always',
      },
      AnnotationCreateFreeText4: {
        dataElement: 'freeTextToolButton4',
        title: 'annotation.freetext',
        img: 'icon-tool-text-free-text',
        group: 'freeTextTools',
        showColor: 'always',
      },
      AnnotationCreateDateFreeText: {
        dataElement: 'dateFreeTextToolButton',
        title: 'annotation.dateFreeText',
        img: 'icon-tool-fill-and-sign-calendar',
        group: 'dateFreeTextTools',
        showColor: 'always',
      },
      AnnotationCreateDateFreeText2: {
        dataElement: 'dateFreeTextToolButton2',
        title: 'annotation.dateFreeText',
        img: 'icon-tool-fill-and-sign-calendar',
        group: 'dateFreeTextTools',
        showColor: 'always',
      },
      AnnotationCreateDateFreeText3: {
        dataElement: 'dateFreeTextToolButton3',
        title: 'annotation.dateFreeText',
        img: 'icon-tool-fill-and-sign-calendar',
        group: 'dateFreeTextTools',
        showColor: 'always',
      },
      AnnotationCreateDateFreeText4: {
        dataElement: 'dateFreeTextToolButton4',
        title: 'annotation.dateFreeText',
        img: 'icon-tool-fill-and-sign-calendar',
        group: 'dateFreeTextTools',
        showColor: 'always',
      },
      AnnotationCreateMarkInsertText: {
        dataElement: 'markInsertTextToolButton',
        title: 'annotation.markInsertText',
        img: 'ic-insert text',
        group: 'markInsertTextTools',
        showColor: 'always',
      },
      AnnotationCreateMarkInsertText2: {
        dataElement: 'markInsertTextToolButton2',
        title: 'annotation.markInsertText',
        img: 'ic-insert text',
        group: 'markInsertTextTools',
        showColor: 'always',
      },
      AnnotationCreateMarkInsertText3: {
        dataElement: 'markInsertTextToolButton3',
        title: 'annotation.markInsertText',
        img: 'ic-insert text',
        group: 'markInsertTextTools',
        showColor: 'always',
      },
      AnnotationCreateMarkInsertText4: {
        dataElement: 'markInsertTextToolButton4',
        title: 'annotation.markInsertText',
        img: 'ic-insert text',
        group: 'markInsertTextTools',
        showColor: 'always',
      },
      AnnotationCreateMarkReplaceText: {
        dataElement: 'markReplaceToolButton',
        title: 'annotation.markReplaceText',
        img: 'ic-replace text',
        group: 'markReplaceTextTools',
        showColor: 'always',
      },
      AnnotationCreateMarkReplaceText2: {
        dataElement: 'markReplaceToolButton2',
        title: 'annotation.markReplaceText',
        img: 'ic-replace text',
        group: 'markReplaceTextTools',
        showColor: 'always',
      },
      AnnotationCreateMarkReplaceText3: {
        dataElement: 'markReplaceToolButton3',
        title: 'annotation.markReplaceText',
        img: 'ic-replace text',
        group: 'markReplaceTextTools',
        showColor: 'always',
      },
      AnnotationCreateMarkReplaceText4: {
        dataElement: 'markReplaceToolButton4',
        title: 'annotation.markReplaceText',
        img: 'ic-replace text',
        group: 'markReplaceTextTools',
        showColor: 'always',
      },
      AnnotationCreateCallout: {
        dataElement: 'calloutToolButton',
        title: 'annotation.callout',
        img: 'icon-tool-callout-line',
        group: 'calloutTools',
        showColor: 'always',
      },
      AnnotationCreateCallout2: {
        dataElement: 'calloutToolButton2',
        title: 'annotation.callout',
        img: 'icon-tool-callout-line',
        group: 'calloutTools',
        showColor: 'always',
      },
      AnnotationCreateCallout3: {
        dataElement: 'calloutToolButton3',
        title: 'annotation.callout',
        img: 'icon-tool-callout-line',
        group: 'calloutTools',
        showColor: 'always',
      },
      AnnotationCreateCallout4: {
        dataElement: 'calloutToolButton4',
        title: 'annotation.callout',
        img: 'icon-tool-callout-line',
        group: 'calloutTools',
        showColor: 'always',
      },
      AnnotationCreateSticky: {
        dataElement: 'stickyToolButton',
        title: 'annotation.stickyNote',
        img: 'icon-tool-comment-line',
        group: 'stickyTools',
        showColor: 'always',
      },
      AnnotationCreateSticky2: {
        dataElement: 'stickyToolButton2',
        title: 'annotation.stickyNote',
        img: 'icon-tool-comment-line',
        group: 'stickyTools',
        showColor: 'always',
      },
      AnnotationCreateSticky3: {
        dataElement: 'stickyToolButton3',
        title: 'annotation.stickyNote',
        img: 'icon-tool-comment-line',
        group: 'stickyTools',
        showColor: 'always',
      },
      AnnotationCreateSticky4: {
        dataElement: 'stickyToolButton4',
        title: 'annotation.stickyNote',
        img: 'icon-tool-comment-line',
        group: 'stickyTools',
        showColor: 'always',
      },
      AnnotationCreateRectangle: {
        dataElement: 'rectangleToolButton',
        title: 'annotation.rectangle',
        img: 'icon-tool-shape-rectangle',
        group: 'rectangleTools',
        showColor: 'always',
      },
      AnnotationCreateRectangle2: {
        dataElement: 'rectangleToolButton2',
        title: 'annotation.rectangle',
        img: 'icon-tool-shape-rectangle',
        group: 'rectangleTools',
        showColor: 'always',
      },
      AnnotationCreateRectangle3: {
        dataElement: 'rectangleToolButton3',
        title: 'annotation.rectangle',
        img: 'icon-tool-shape-rectangle',
        group: 'rectangleTools',
        showColor: 'always',
      },
      AnnotationCreateRectangle4: {
        dataElement: 'rectangleToolButton4',
        title: 'annotation.rectangle',
        img: 'icon-tool-shape-rectangle',
        group: 'rectangleTools',
        showColor: 'always',
      },
      AnnotationCreateEllipse: {
        dataElement: 'ellipseToolButton',
        title: 'annotation.ellipse',
        img: 'icon-tool-shape-oval',
        group: 'ellipseTools',
        showColor: 'always',
      },
      AnnotationCreateEllipse2: {
        dataElement: 'ellipseToolButton2',
        title: 'annotation.ellipse',
        img: 'icon-tool-shape-oval',
        group: 'ellipseTools',
        showColor: 'always',
      },
      AnnotationCreateEllipse3: {
        dataElement: 'ellipseToolButton3',
        title: 'annotation.ellipse',
        img: 'icon-tool-shape-oval',
        group: 'ellipseTools',
        showColor: 'always',
      },
      AnnotationCreateEllipse4: {
        dataElement: 'ellipseToolButton4',
        title: 'annotation.ellipse',
        img: 'icon-tool-shape-oval',
        group: 'ellipseTools',
        showColor: 'always',
      },
      AnnotationCreateArc: {
        dataElement: 'arcToolButton',
        title: 'annotation.arc',
        img: 'icon-tool-arc',
        group: 'arcTools',
        showColor: 'always',
      },
      AnnotationCreateArc2: {
        dataElement: 'arcToolButton2',
        title: 'annotation.arc',
        img: 'icon-tool-arc',
        group: 'arcTools',
        showColor: 'always',
      },
      AnnotationCreateArc3: {
        dataElement: 'arcToolButton3',
        title: 'annotation.arc',
        img: 'icon-tool-arc',
        group: 'arcTools',
        showColor: 'always',
      },
      AnnotationCreateArc4: {
        dataElement: 'arcToolButton4',
        title: 'annotation.arc',
        img: 'icon-tool-arc',
        group: 'arcTools',
        showColor: 'always',
      },
      AnnotationCreateLine: {
        dataElement: 'lineToolButton',
        title: 'annotation.line',
        img: 'icon-tool-shape-line',
        group: 'lineTools',
        showColor: 'always',
      },
      AnnotationCreateLine2: {
        dataElement: 'lineToolButton2',
        title: 'annotation.line',
        img: 'icon-tool-shape-line',
        group: 'lineTools',
        showColor: 'always',
      },
      AnnotationCreateLine3: {
        dataElement: 'lineToolButton3',
        title: 'annotation.line',
        img: 'icon-tool-shape-line',
        group: 'lineTools',
        showColor: 'always',
      },
      AnnotationCreateLine4: {
        dataElement: 'lineToolButton4',
        title: 'annotation.line',
        img: 'icon-tool-shape-line',
        group: 'lineTools',
        showColor: 'always',
      },
      AnnotationCreatePolyline: {
        dataElement: 'polylineToolButton',
        title: 'annotation.polyline',
        img: 'icon-tool-shape-polyline',
        group: 'polyLineTools',
        showColor: 'always',
      },
      AnnotationCreatePolyline2: {
        dataElement: 'polylineToolButton2',
        title: 'annotation.polyline',
        img: 'icon-tool-shape-polyline',
        group: 'polyLineTools',
        showColor: 'always',
      },
      AnnotationCreatePolyline3: {
        dataElement: 'polylineToolButton3',
        title: 'annotation.polyline',
        img: 'icon-tool-shape-polyline',
        group: 'polyLineTools',
        showColor: 'always',
      },
      AnnotationCreatePolyline4: {
        dataElement: 'polylineToolButton4',
        title: 'annotation.polyline',
        img: 'icon-tool-shape-polyline',
        group: 'polyLineTools',
        showColor: 'always',
      },
      AnnotationCreatePolygon: {
        dataElement: 'polygonToolButton',
        title: 'annotation.polygon',
        img: 'icon-tool-shape-polygon',
        group: 'polygonTools',
        showColor: 'always',
      },
      AnnotationCreatePolygon2: {
        dataElement: 'polygonToolButton2',
        title: 'annotation.polygon',
        img: 'icon-tool-shape-polygon',
        group: 'polygonTools',
        showColor: 'always',
      },
      AnnotationCreatePolygon3: {
        dataElement: 'polygonToolButton3',
        title: 'annotation.polygon',
        img: 'icon-tool-shape-polygon',
        group: 'polygonTools',
        showColor: 'always',
      },
      AnnotationCreatePolygon4: {
        dataElement: 'polygonToolButton4',
        title: 'annotation.polygon',
        img: 'icon-tool-shape-polygon',
        group: 'polygonTools',
        showColor: 'always',
      },
      AnnotationCreatePolygonCloud: {
        dataElement: 'cloudToolButton',
        title: 'annotation.polygonCloud',
        img: 'icon-tool-shape-cloud',
        group: 'cloudTools',
        showColor: 'always',
      },
      AnnotationCreatePolygonCloud2: {
        dataElement: 'cloudToolButton2',
        title: 'annotation.polygonCloud',
        img: 'icon-tool-shape-cloud',
        group: 'cloudTools',
        showColor: 'always',
      },
      AnnotationCreatePolygonCloud3: {
        dataElement: 'cloudToolButton4',
        title: 'annotation.polygonCloud',
        img: 'icon-tool-shape-cloud',
        group: 'cloudTools',
        showColor: 'always',
      },
      AnnotationCreatePolygonCloud4: {
        dataElement: 'cloudToolButton5',
        title: 'annotation.polygonCloud',
        img: 'icon-tool-shape-cloud',
        group: 'cloudTools',
        showColor: 'always',
      },
      [ToolNames.CALIBRATION_MEASUREMENT]: {
        dataElement: 'calibrationToolButton',
        title: 'annotation.calibration',
        img: 'icon-tool-measurement-distance-line',
        group: 'calibrationTools',
        showColor: 'never',
      },
      AnnotationCreateArrow: {
        dataElement: 'arrowToolButton',
        title: 'annotation.arrow',
        img: 'icon-tool-shape-arrow',
        group: 'arrowTools',
        showColor: 'always',
      },
      AnnotationCreateArrow2: {
        dataElement: 'arrowToolButton2',
        title: 'annotation.arrow',
        img: 'icon-tool-shape-arrow',
        group: 'arrowTools',
        showColor: 'always',
      },
      AnnotationCreateArrow3: {
        dataElement: 'arrowToolButton3',
        title: 'annotation.arrow',
        img: 'icon-tool-shape-arrow',
        group: 'arrowTools',
        showColor: 'always',
      },
      AnnotationCreateArrow4: {
        dataElement: 'arrowToolButton4',
        title: 'annotation.arrow',
        img: 'icon-tool-shape-arrow',
        group: 'arrowTools',
        showColor: 'always',
      },
      AnnotationCreateSignature: {
        dataElement: 'signatureToolButton',
        title: 'annotation.signature',
        img: 'icon-tool-signature',
        group: 'signatureTools',
        showColor: 'never',
      },
      AnnotationCreateFileAttachment: {
        dataElement: 'fileAttachmentToolButton',
        title: 'annotation.fileattachment',
        img: 'ic_fileattachment_24px',
        group: 'fileAttachmentTools',
        showColor: 'never',
      },
      AnnotationCreateThreeD: {
        dataElement: 'threeDToolGroupButton',
        title: 'annotation.3D',
        img: 'icon-tool-model3d',
        group: 'model3DTools',
        showColor: 'never',
      },
      AnnotationCreateStamp: {
        dataElement: 'stampToolButton',
        title: 'annotation.stamp',
        img: 'icon-tool-image-line',
        group: 'stampTools',
        showColor: 'active',
      },
      AnnotationCreateRubberStamp: {
        dataElement: 'rubberStampToolButton',
        title: 'annotation.rubberStamp',
        img: 'icon-tool-stamp-line',
        group: 'rubberStampTools',
        showColor: 'active',
      },
      [ToolNames.FORM_FILL_CROSS]: {
        dataElement: 'crossStampToolButton',
        title: 'annotation.formFillCross',
        img: 'icon-tool-cross-stamp',
        group: 'crossStampTools',
        showColor: 'active',
      },
      [ToolNames.FORM_FILL_CHECKMARK]: {
        dataElement: 'checkStampToolButton',
        title: 'annotation.formFillCheckmark',
        img: 'icon-tool-check-stamp',
        group: 'checkStampTools',
        showColor: 'active',
      },
      [ToolNames.FORM_FILL_DOT]: {
        dataElement: 'dotStampToolButton',
        title: 'annotation.formFillDot',
        img: 'icon-tool-dot-stamp',
        group: 'dotStampTools',
        showColor: 'active',
      },
      CropPage: {
        dataElement: 'cropToolButton',
        title: 'annotation.crop',
        img: 'ic_crop_black_24px',
        showColor: 'never',
        group: 'cropTools',
      },
      SnippingTool: {
        dataElement: 'snippingToolButton',
        title: 'annotation.snipping',
        img: 'ic_snipping_black_24px',
        showColor: 'never',
        group: 'snippingTools',
      },
      ContentEditTool: {
        dataElement: 'contentEditToolButton',
        title: 'action.edit',
        img: 'ic_edit_page_24px',
        showColor: 'never',
        group: 'contentEditTools',
      },
      AddParagraphTool: {
        dataElement: 'addParagraphToolButton',
        title: 'action.addParagraph',
        img: 'ic-paragraph',
        showColor: 'never',
        group: 'addParagraphTools',
      },
      [ToolNames.ADD_IMAGE_CONTENT]: {
        dataElement: 'addImageContentToolButton',
        title: 'annotation.newImage',
        img: 'icon-tool-image-line',
        showColor: 'never',
        group: 'addImageContentTools',
      },
      AnnotationCreateRedaction: {
        dataElement: 'redactionButton',
        title: 'option.redaction.markForRedaction',
        img: 'icon-tool-select-area-redaction',
        group: 'redactionTools',
        showColor: 'always',
      },
      AnnotationCreateRedaction2: {
        dataElement: 'redactionButton2',
        title: 'option.redaction.markForRedaction',
        img: 'icon-tool-select-area-redaction',
        group: 'redactionTools',
        showColor: 'always',
      },
      AnnotationCreateRedaction3: {
        dataElement: 'redactionButton3',
        title: 'option.redaction.markForRedaction',
        img: 'icon-tool-select-area-redaction',
        group: 'redactionTools',
        showColor: 'always',
      },
      AnnotationCreateRedaction4: {
        dataElement: 'redactionButton4',
        title: 'option.redaction.markForRedaction',
        img: 'icon-tool-select-area-redaction',
        group: 'redactionTools',
        showColor: 'always',
      },
      Pan: { dataElement: 'panToolButton', title: 'tool.pan', img: 'icon-header-pan', showColor: 'never' },
      AnnotationEdit: {
        dataElement: 'selectToolButton',
        title: 'tool.select',
        img: 'multi select',
        showColor: 'never',
      },
      TextSelect: { dataElement: 'textSelectButton', img: 'icon-header-select-line', showColor: 'never' },
      OfficeEditorContentSelect: {
        dataElement: 'textSelectButton',
        img: 'icon-header-select-line',
        showColor: 'never',
      },
      MarqueeZoomTool: {
        dataElement: 'marqueeToolButton',
        showColor: 'never',
        img: 'icon-header-zoom-marquee',
      },
      AnnotationEraserTool: {
        dataElement: 'eraserToolButton',
        title: 'annotation.eraser',
        img: 'icon-operation-eraser',
        showColor: 'never',
      },
      TextFormFieldCreateTool: {
        dataElement: 'textFieldToolButton',
        title: 'annotation.textField',
        img: 'icon-form-field-text',
        group: 'formFieldTools',
        showColor: 'always',
      },
      TextFormFieldCreateTool2: {
        dataElement: 'textFieldToolButton2',
        title: 'annotation.textField',
        img: 'icon-form-field-text',
        group: 'formFieldTools',
        showColor: 'always',
      },
      TextFormFieldCreateTool3: {
        dataElement: 'textFieldToolButton3',
        title: 'annotation.textField',
        img: 'icon-form-field-text',
        group: 'formFieldTools',
        showColor: 'always',
      },
      TextFormFieldCreateTool4: {
        dataElement: 'textFieldToolButton4',
        title: 'annotation.textField',
        img: 'icon-form-field-text',
        group: 'formFieldTools',
        showColor: 'always',
      },
      SignatureFormFieldCreateTool: {
        dataElement: 'signatureFieldToolButton',
        title: 'annotation.signatureFormField',
        img: 'icon-form-field-signature',
        group: 'sigFieldTools',
        showColor: 'always',
      },
      SignatureFormFieldCreateTool2: {
        dataElement: 'signatureFieldToolButton2',
        title: 'annotation.signatureFormField',
        img: 'icon-form-field-signature',
        group: 'sigFieldTools',
        showColor: 'always',
      },
      SignatureFormFieldCreateTool3: {
        dataElement: 'signatureFieldToolButton3',
        title: 'annotation.signatureFormField',
        img: 'icon-form-field-signature',
        group: 'sigFieldTools',
        showColor: 'always',
      },
      SignatureFormFieldCreateTool4: {
        dataElement: 'signatureFieldToolButton4',
        title: 'annotation.signatureFormField',
        img: 'icon-form-field-signature',
        group: 'sigFieldTools',
        showColor: 'always',
      },
      CheckBoxFormFieldCreateTool: {
        dataElement: 'checkBoxFieldCreateToolButton',
        title: 'annotation.checkBoxFormField',
        img: 'icon-form-field-checkbox',
        group: 'checkBoxFieldTools',
        showColor: 'always',
      },
      RadioButtonFormFieldCreateTool: {
        dataElement: 'radioButtonFieldCreateToolButton',
        title: 'annotation.radioButtonFormField',
        img: 'icon-form-field-radiobutton',
        group: 'radioButtonFieldTools',
        showColor: 'always',
      },
      ListBoxFormFieldCreateTool: {
        dataElement: 'listBoxFieldCreateToolButton',
        title: 'annotation.listBoxFormField',
        img: 'icon-form-field-listbox',
        group: 'listBoxFieldTools',
        showColor: 'always',
      },
      ListBoxFormFieldCreateTool2: {
        dataElement: 'listBoxFieldCreateToolButton2',
        title: 'annotation.listBoxFormField',
        img: 'icon-form-field-listbox',
        group: 'listBoxFieldTools',
        showColor: 'always',
      },
      ListBoxFormFieldCreateTool3: {
        dataElement: 'listBoxFieldCreateToolButton3',
        title: 'annotation.listBoxFormField',
        img: 'icon-form-field-listbox',
        group: 'listBoxFieldTools',
        showColor: 'always',
      },
      ListBoxFormFieldCreateTool4: {
        dataElement: 'listBoxFieldCreateToolButton4',
        title: 'annotation.listBoxFormField',
        img: 'icon-form-field-listbox',
        group: 'listBoxFieldTools',
        showColor: 'always',
      },
      ComboBoxFormFieldCreateTool: {
        dataElement: 'comboBoxFieldCreateToolButton',
        title: 'annotation.comboBoxFormField',
        img: 'icon-form-field-combobox',
        group: 'comboBoxFieldTools',
        showColor: 'always',
      },
      ComboBoxFormFieldCreateTool2: {
        dataElement: 'comboBoxFieldCreateToolButton2',
        title: 'annotation.comboBoxFormField',
        img: 'icon-form-field-combobox',
        group: 'comboBoxFieldTools',
        showColor: 'always',
      },
      ComboBoxFormFieldCreateTool3: {
        dataElement: 'comboBoxFieldCreateToolButton3',
        title: 'annotation.comboBoxFormField',
        img: 'icon-form-field-combobox',
        group: 'comboBoxFieldTools',
        showColor: 'always',
      },
      ComboBoxFormFieldCreateTool4: {
        dataElement: 'comboBoxFieldCreateToolButton4',
        title: 'annotation.comboBoxFormField',
        img: 'icon-form-field-combobox',
        group: 'comboBoxFieldTools',
        showColor: 'always',
      },
      CheckBoxFormFieldCreateTool2: {
        dataElement: 'checkBoxFieldToolGroupButton2',
        title: 'annotation.checkBoxFormField',
        img: 'icon-form-field-checkbox',
        group: 'checkBoxFieldTools',
        showColor: 'always',
      },
      CheckBoxFormFieldCreateTool3: {
        dataElement: 'checkBoxFieldToolGroupButton3',
        title: 'annotation.checkBoxFormField',
        img: 'icon-form-field-checkbox',
        group: 'checkBoxFieldTools',
        showColor: 'always',
      },
      CheckBoxFormFieldCreateTool4: {
        dataElement: 'checkBoxFieldToolGroupButton4',
        title: 'annotation.checkBoxFormField',
        img: 'icon-form-field-checkbox',
        group: 'checkBoxFieldTools',
        showColor: 'always',
      },
      RadioButtonFormFieldCreateTool2: {
        dataElement: 'radioButtonFieldCreateToolButton2',
        title: 'annotation.radioButtonFormField',
        img: 'icon-form-field-radiobutton',
        group: 'radioButtonFieldTools',
        showColor: 'always',
      },
      RadioButtonFormFieldCreateTool3: {
        dataElement: 'radioButtonFieldCreateToolButton3',
        title: 'annotation.radioButtonFormField',
        img: 'icon-form-field-radiobutton',
        group: 'radioButtonFieldTools',
        showColor: 'always',
      },
      RadioButtonFormFieldCreateTool4: {
        dataElement: 'radioButtonFieldCreateToolButton4',
        title: 'annotation.radioButtonFormField',
        img: 'icon-form-field-radiobutton',
        group: 'radioButtonFieldTools',
        showColor: 'always',
      },
      AnnotationCreateChangeViewTool: {
        dataElement: 'changeViewCreateToolButton',
        title: 'annotation.changeView',
        img: 'icon-tool-changeview',
        group: 'changeViewTools',
        showColor: 'always',
      },
      AnnotationCreateChangeViewTool2: {
        dataElement: 'changeViewCreateToolButton2',
        title: 'annotation.changeView',
        img: 'icon-tool-changeview',
        group: 'changeViewTools',
        showColor: 'always',
      },
      AnnotationCreateChangeViewTool3: {
        dataElement: 'changeViewCreateToolButton3',
        title: 'annotation.changeView',
        img: 'icon-tool-changeview',
        group: 'changeViewTools',
        showColor: 'always',
      },
      AnnotationCreateChangeViewTool4: {
        dataElement: 'changeViewCreateToolButton4',
        title: 'annotation.changeView',
        img: 'icon-tool-changeview',
        group: 'changeViewTools',
        showColor: 'always',
      },
      [ToolNames.OUTLINE_DESTINATION]: {
        group: '',
      },
    },
    tab: {
      signatureModal: 'inkSignaturePanelButton',
      pageReplacementModal: 'urlInputPanelButton',
      linkModal: 'URLPanelButton',
      rubberStampTab: 'standardStampPanelButton',
      filterAnnotModal: DataElements.ANNOTATION_USER_FILTER_PANEL_BUTTON,
      [DataElements.SETTINGS_MODAL]: DataElements.SETTINGS_GENERAL_BUTTON,
      savedSignatures: DataElements.SAVED_SIGNATURES_PANEL_BUTTON,
      openFileModal: 'urlInputPanelButton',
      insertPageModal: 'insertBlankPagePanelButton',
      [DataElements.CREATE_PORTFOLIO_MODAL]: DataElements.PORTFOLIO_UPLOAD_FILES_TAB,
    },
    customElementOverrides: {},
    activeHeaderGroup: 'default',
    activeToolName: 'AnnotationEdit',
    activeToolStyles: {},
    customColors:
      localStorageManager.isLocalStorageEnabled() && window.localStorage.getItem(`${instanceId}-customColors`)
        ? JSON.parse(window.localStorage.getItem(`${instanceId}-customColors`))
        : [],
    activeLeftPanel: 'thumbnailsPanel',
    activeTabInPanel: {},
    activeToolGroup: '',
    notePopupId: '',
    isNoteEditing: false,
    fitMode: '',
    rotation: 0,
    displayMode: 'Single',
    currentPage: 1,
    sortStrategy: 'position',
    isFullScreen: false,
    isMultipleViewerMerging: false,
    isThumbnailMerging: true,
    isThumbnailReordering: true,
    isThumbnailMultiselect: true,
    isNotesPanelMultiSelectEnabled: true,
    isOutlineEditingEnabled: true,
    enableNotesPanelVirtualizedList: true,
    notesShowLastUpdatedDate: false,
    allowPageNavigation: true,
    enableToolGroupReordering: true,
    enableNoteSubmissionWithEnter: false,
    isNotesPanelTextCollapsingEnabled: true,
    isNotesPanelRepliesCollapsingEnabled: true,
    isMeasurementAnnotationFilterEnabled: false,
    isCommentThreadExpansionEnabled: false,
    enableMouseWheelZoom: true,
    doesAutoLoad: getHashParameters('auto_load', true),
    isReadOnly: getHashParameters('readonly', false),
    customModals: [],
    customPanels: [],
    genericPanels: defaultPanels,
    useEmbeddedPrint: false,
    useClientSidePrint: false,
    pageLabels: [],
    selectedThumbnailPageIndexes: [],
    shiftKeyThumbnailPivotIndex: null,
    noteDateFormat: defaultNoteDateFormat,
    printedNoteDateFormat: defaultPrintedNoteDateFormat,
    colorMap: copyMapWithDataProperties('currentStyleTab', 'iconColor'),
    warning: {},
    customNoteFilter: null,
    internalNoteFilter: null,
    inlineCommentFilter: annot => {
      const isAnnotationInstanceOf = defaultToolsWithInlineComment.some(
        annotationInstance => annot instanceof annotationInstance,
      );
      return isAnnotationInstanceOf;
    },
    zoomList: defaultZoomList,
    isAccessibleMode: getHashParameters('accessibleMode', false),
    shouldAddA11yContentToDOM: getHashParameters('accessibleMode', false),
    disabledFeaturesInAccessibleReadingMode: {},
    measurementUnits: {
      from: ['in', 'mm', 'cm', 'pt'],
      to: ['in', 'mm', 'cm', 'pt', 'ft', 'ft-in', 'm', 'yd', 'km', 'mi'],
    },
    measurementScalePreset: getMeasurementScalePreset(),
    isMultipleScalesMode: true,
    maxSignaturesCount: 10,
    signatureFonts: ['Satisfy', 'Nothing-You-Could-Do', 'La-Belle-Aurore', 'Whisper'],
    isReplyDisabledFunc: null,
    userData: [
      {
        id: 1,
        full_name: 'Gaurav Singh',
        email: 'gaurav@example.com',
        value: 'Gaurav Singh',
      },
      {
        id: 2,
        full_name: 'John Smith',
        email: 'john@example.com',
        value: 'John Smith',
      },
      {
        id: 3,
        full_name: 'Jane Doe',
        email: 'jane@example.com',
        value: 'Jane Doe',
      },
    ],
    customMeasurementOverlay: [],
    noteTransformFunction: null,
    standardStamps: [],
    customStamps: [],
    selectedStampIndex: 0,
    lastSelectedStampIndex: 0,
    signatureMode: SignatureModes.FULL_SIGNATURE,
    savedSignatures: [],
    savedInitials: [],
    displayedSignaturesFilterFunction: () => true,
    selectedDisplayedSignatureIndex: 0,
    selectedDisplayedInitialsIndex: 0,
    annotationContentOverlayHandler: null,
    isSnapModeEnabled: false,
    isReaderMode: false,
    unreadAnnotationIdSet: new Set(),
    watermarkModalOptions: null,
    fonts: [...defaultFonts, ...webFonts],
    shouldResetAudioPlaybackPosition: false,
    activeSoundAnnotation: null,
    embeddedJSPopupStyle: {},
    shouldShowApplyCropWarning: true,
    shouldShowApplySnippingWarning: true,
    presetCropDimensions,
    presetNewPageDimensions,
    dateTimeFormats: defaultDateTimeFormats,
    thumbnailSelectionMode: 'checkbox',
    annotationFilters: {
      includeReplies: true,
      isDocumentFilterActive: false,
      authorFilter: [],
      colorFilter: [],
      typeFilter: [],
      statusFilter: [],
    },
    zoomStepFactors: [
      {
        step: 7.5,
        startZoom: 0,
      },
      {
        step: 25,
        startZoom: 80,
      },
      {
        step: 50,
        startZoom: 150,
      },
      {
        step: 100,
        startZoom: 250,
      },
      {
        step: 200,
        startZoom: 400,
      },
      {
        step: 400,
        startZoom: 800,
      },
      {
        step: 800,
        startZoom: 3200,
      },
      {
        step: 1600,
        startZoom: 6400,
      },
    ],
    contentBoxEditor: null,
    notesPanelCustomHeaderOptions: null,
    notesPanelCustomEmptyPanel: null,
    replyAttachmentPreviewEnabled: true,
    savedSignatureTabEnabled: false,
    replyAttachmentHandler: null,
    customSettings: [],
    modularComponentStash: {},
    modularHeaders: defaultModularHeaders,
    modularComponents: defaultModularComponents,
    modularComponentFunctions: {},
    activeGroupedItems: [],
    activeCustomRibbon: '',
    fixedGroupedItems: [],
    modularHeadersHeight: {
      topHeaders: 49,
      bottomHeaders: 45,
    },
    modularHeadersWidth: {
      rightHeader: 0,
      leftHeader: 0,
    },
    customElementSizes: {},
    floatingContainersDimensions: {
      topFloatingContainerHeight: 0,
      bottomFloatingContainerHeight: 0,
    },
    toolDefaultStyleUpdateFromAnnotationPopupEnabled: true,
    annotationToolStyleSyncingEnabled: false,
    shortcutKeyMap: { ...ShortcutKeys },
    flyoutMap: defaultFlyoutMap,
    flyoutPosition: { x: 0, y: 0 },
    activeFlyout: null,
    flyoutToggleElement: null,
    textSignatureCanvasMultiplier: 1,
    isShowComparisonButtonEnabled: false,
    isMultiViewerModeAvailable: false,
    isOfficeEditorMode: false,
    isOfficeEditorHeaderEnabled: false,
    isSpreadsheetEditorModeEnabled: false,
    colors: initialColors,
    textColors: initialTextColors,
    toolColorOverrides: {},
    defaultPrintMargins: '0',
    scaleOverlayPosition: 'top-right',
    focusedElementsStack: [],
    isKeyboardOpen: false,
  },
  search: {
    value: '',
    replaceValue: '',
    nextResult: null,
    isCaseSensitive: false,
    isWholeWord: false,
    isWildcard: false,
    isRegex: false,
    isSearchUp: false,
    isAmbientString: false,
    clearSearchPanelOnClose: false,
    results: [],
    redactionSearchPatterns: {
      creditCards: {
        label: 'redactionPanel.search.creditCards',
        icon: 'redact-icons-credit-card',
        type: redactionTypeMap['CREDIT_CARD'],
        regex: /\b(?:\d[ -]*?){13,16}\b/,
      },
      phoneNumbers: {
        label: 'redactionPanel.search.phoneNumbers',
        icon: 'redact-icons-phone-number',
        type: redactionTypeMap['PHONE'],
        regex:
          /\d?(\s?|-?|\+?|\.?)((\(\d{1,4}\))|(\d{1,3})|\s?)(\s?|-?|\.?)((\(\d{1,3}\))|(\d{1,3})|\s?)(\s?|-?|\.?)((\(\d{1,3}\))|(\d{1,3})|\s?)(\s?|-?|\.?)\d{3}(-|\.|\s)\d{4,5}/,
      },
      emails: {
        label: 'redactionPanel.search.emails',
        icon: 'redact-icons-email',
        type: redactionTypeMap['EMAIL'],
        regex: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,6}\b/,
      },
    },
  },
  document: {
    totalPages: {
      1: 0,
      2: 0,
    },
    outlines: [],
    bookmarks: {},
    portfolio: [],
    layers: [],
    printQuality: 1,
    passwordAttempts: -1,
    maxPasswordAttempts: 3,
    loadingProgress: 0,
  },
  user: {
    name: getHashParameters('user', 'Guest'),
    isAdmin: getHashParameters('admin', false),
  },
  advanced: {
    customCSS: getHashParameters('css', null),
    defaultDisabledElements: getHashParameters('disabledElements', ''),
    fullAPI: getHashParameters('pdfnet', false),
    preloadWorker: getHashParameters('preloadWorker', false),
    enableOptimizedWorkers: getHashParameters('enableOptimizedWorkers', true),
    serverUrl: getHashParameters('server_url', ''),
    serverUrlHeaders: JSON.parse(getHashParameters('serverUrlHeaders', '{}')),
    useSharedWorker: getHashParameters('useSharedWorker', false),
    disableI18n: getHashParameters('disableI18n', false),
    pdfWorkerTransportPromise: null,
    officeWorkerTransportPromise: null,
    disableIndexedDB: getHashParameters('disableIndexedDB', false),
    disableMultiViewerComparison:
      getHashParameters('disableMultiViewerComparison', false) || !getHashParameters('pdfnet', false),
  },
  featureFlags: {
    customizableUI: false,
  },
  wv3dPropertiesPanel: {
    modelData: [],
    schema: {
      headerName: 'Name',
      defaultValues: {},
      groups: {},
      groupOrder: [],
      removeEmptyRows: false,
      removeEmptyGroups: false,
      createRawValueGroup: true,
    },
  },
  officeEditor: {
    cursorProperties: {
      paragraphProperties: {},
      locationProperties: {},
    },
    selectionProperties: {
      paragraphProperties: {},
    },
    availableFontFaces,
    cssFontValues,
    editMode: OfficeEditorEditMode.EDITING,
  },
  digitalSignatureValidation: {
    validationModalWidgetName: '',
    verificationResult: {},
    certificates: [],
    trustLists: [],
    isRevocationCheckingEnabled: false,
    revocationProxyPrefix: null,
  },
  spreadsheetEditor: {
    activeCellRange: '',
    cellProperties: {
      cellType: null,
      cellFormula: null,
      stringCellValue: null,
      topLeftRow: null,
      topLeftColumn: null,
      bottomRightRow: null,
      bottomRightColumn: null,
    },
  },
  issue: {
    issues: [
      {
        'id': 309,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Clarify MSS types',
        'full_text':
          'The listed MSS types for horizontal piping hangers and supports do not specify whether they are for insulated or non-insulated pipes, which should be clarified.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'page_number': 45,
        'issue_type': 'spec',
        'csi_code': '230529',
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 308,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Specify insert standard',
        'full_text':
          'It is recommended to specify the standard that the thermal-hanger shield inserts should comply with.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'page_number': 78,
        'issue_type': 'drawing',
        'drawing_sheet_id': 12,
        'drawing_sheet': {
          'id': 12,
          'sheet_number': 'A-101',
          'title': 'Ground Floor Plan',
        },
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 306,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Incomplete sentence',
        'full_text':
          "The phrase 'Comply with manufacturer requirements and CPC' is incomplete and should be revised to provide specific requirements.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'page_number': 23,
        'issue_type': 'overlay',
        'overlay_execution_id': 456,
        'overlay_sheets': ['A-101', 'A-102', 'M-201'],
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      // spec
      {
        'id': 305,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': 1,
        'creator': {
          'id': 1,
          'full_name': 'Gaurav Singh',
          'role': 'admin',
        },
        'assignee_id': 2,
        'assignee': {
          'id': 2,
          'full_name': 'John Smith',
          'role': 'engineer',
        },
        'summary': 'Define sealant types',
        'full_text':
          "The specification mentions both 'joint sealant' and 'flashing sealant' without defining what these are or where each should be used.",
        'is_favourite': false,
        'xfdf': null,
        'page_number': 156,
        'issue_type': 'spec',
        'csi_code': '079200',
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 304,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Clarify schedule priority',
        'full_text':
          "The spec refers to both 'Piping Insulation Schedule, General' and also to more specific schedules, but it is unclear which schedule takes precedence or how they relate.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 301,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Specify insert standard',
        'full_text':
          'It is recommended to specify the standard that the thermal-hanger shield inserts should comply with.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 299,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Clarify LEED intent',
        'full_text':
          "In section 018113, the specifier should clarify if the LEED rating is 'uncertified' or if the project is pursuing LEED certification.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 298,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Verify acronym reference',
        'full_text':
          "In section 014000, the specifier should verify the acronym reference to Gale's Encyclopedia of Associations.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 297,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Verify Project Site',
        'full_text':
          "In section 014000, the specifier should verify the definition provided for 'Project Site' with the drawings.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 293,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Incorrect table entry',
        'full_text':
          "The Table of Contents includes 'SCEPSCEA CYP AECSPECS 44776.719' which does not appear to be a section.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 281,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Transformer label unclear',
        'full_text':
          'The specification requires labels for transformers to include the tag designation shown on Drawings for the transformer, feeder, and panelboards or equipment supplied by the secondary, but it is not clear if this applies to both dry-type and liquid-filled transformers.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 280,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 5,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Finish for exterior lighting',
        'full_text':
          'The document does not specify the type of finish for the metal parts of the exterior lighting fixtures, which could affect the aesthetic and durability of the fixtures.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 279,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 5,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Finish for metal parts',
        'full_text':
          'The document does not specify the type of finish for the metal parts of the LED interior lighting fixtures, which could affect the aesthetic and durability of the fixtures.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 278,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 5,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Mounting accessories for switches',
        'full_text':
          'The document does not specify the type of mounting accessories required for outdoor photoelectric switches, which could affect the installation and performance of the switches.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 277,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Label size for elevated components',
        'full_text':
          'The document does not specify the required size for labels and letters for elevated components, which should be appropriate for viewing from the floor.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 276,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 3,
        'discipline_id': 1,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Sealing instructions missing',
        'full_text':
          'The document does not provide specific instructions for sealing underground, exterior-wall and floor penetrations, which could lead to potential issues with water ingress or structural integrity.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 275,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 3,
        'discipline_id': 1,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Missing expansion fittings',
        'full_text':
          'The document does not specify the use of expansion fittings for underground, exterior-wall and floor penetrations, which could lead to potential issues with water ingress or structural integrity.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 274,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified hardware finishes',
        'full_text':
          'The document does not specify the exact finishes for the door hardware, only that they should comply with ANSI/BHMA A156.18. This could lead to inconsistencies in the appearance of the hardware.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 273,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Guideline-only hardware sets',
        'full_text':
          'The document specifies that the door hardware sets are a guideline only and should not be considered a detailed hardware schedule. This could lead to confusion or misinterpretation during installation.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 272,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Verify acronym reference',
        'full_text': 'In section 060573.13, the specifier should verify the acronym reference to CCR 17-93120.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 271,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 1,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Installation details missing',
        'full_text':
          'The specification for composite decking does not include detailed information on the installation process, which could lead to improper installation and potential safety hazards.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 269,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Incorrect CSI code',
        'full_text':
          "The document specifies 'Fire-Retardant Wood Treatment' under Division 06 - Wood and Plastic with the CSI code 060573.13. However, the correct CSI code for fire-retardant wood treatment is 060573. This discrepancy could cause issues in locating the correct section during construction.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 268,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Missing install instructions',
        'full_text':
          "The specification refers to 'recessed pavement markings' in Part 2, but there is no corresponding mention of them in Part 3 (Execution).",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 262,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Clarify standard priority',
        'full_text':
          "The specification refers to both 'HVAC Duct Construction Standards--Metal and Flexible' and also to more specific standards, but it is unclear which standard takes precedence or how they relate.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 260,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Incomplete sentence',
        'full_text':
          "The phrase 'Subject to compliance with requirements, available TAB contractors that may be engaged include, but are not limited to, the following:' is incomplete and should be revised to provide specific requirements or remove the language.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 259,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Specify label material',
        'full_text':
          'The material for equipment labels is not specified, so it is unclear whether the labels should be metal or plastic.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 258,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 3,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Missing acceleration values',
        'full_text':
          'The seismic-restraint loading section lacks specific values for design spectral response acceleration, which are needed for calculations.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 257,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Add product specs',
        'full_text':
          "The phrase 'SEE PRODUCT SPECIFICATIONS AT END OF SECTION' is vague and should be replaced with specific product requirements.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 256,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Specify label material',
        'full_text': 'It should be clarified whether the equipment labels should be metal or plastic.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 255,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 2,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Verify NOMMA guidelines',
        'full_text':
          'In section 057110, the specifier should verify the NOMMA guideline references for AESS conformance.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 254,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 2,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Verify NOMMA guidelines',
        'full_text':
          'In section 055000, the specifier should verify the NOMMA guideline references for AESS conformance.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 253,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Verify exposure classes',
        'full_text':
          'In section 033000, the specifier should verify the ACI 318 exposure class designations with the drawings.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 250,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Clarify submittal recipient',
        'full_text': 'In section 017700, the specifier should clarify who the submittals should be made to.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 249,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Verify AIA documents',
        'full_text':
          'In section 011100, the specifier should verify that the AIA document family matches the type of contract used.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 248,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Clarify submittal numbering',
        'full_text': "In section 006211, the specifier should clarify what is meant by 'GC or CM Submittal#'.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 247,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Verify AIA documents',
        'full_text':
          'In section 006000, the specifier should verify that the AIA document family matches the type of contract used.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 245,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 4,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Finish for decorative fence',
        'full_text':
          'The document does not specify the type of finish for the decorative metal fence, which could affect the aesthetic and durability of the fence.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 244,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified MPI standards',
        'full_text':
          'The document specifies that the painting and coating should conform to MPI standards, but does not specify which MPI standards apply, which could lead to confusion.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 243,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified manufacturer instructions',
        'full_text':
          "The document specifies that the vinyl wall coverings should be installed according to the manufacturer's instructions, but does not specify which manufacturer's instructions to follow, which could lead to inconsistencies.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 242,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified manufacturer instructions',
        'full_text':
          "The document specifies that the sheet carpeting should be installed according to the manufacturer's instructions, but does not specify which manufacturer's instructions to follow, which could lead to inconsistencies.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 241,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified manufacturer instructions',
        'full_text':
          "The document specifies that the resilient tile flooring should be installed according to the manufacturer's instructions, but does not specify which manufacturer's instructions to follow, which could lead to inconsistencies.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 240,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified manufacturer instructions',
        'full_text':
          "The document specifies that the resilient sheet flooring should be installed according to the manufacturer's instructions, but does not specify which manufacturer's instructions to follow, which could lead to inconsistencies.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 239,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified manufacturer instructions',
        'full_text':
          "The document specifies that the resilient base and accessories should be installed according to the manufacturer's instructions, but does not specify which manufacturer's instructions to follow, which could lead to inconsistencies.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 238,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified manufacturer instructions',
        'full_text':
          "The document specifies that the wood flooring should be installed according to the manufacturer's instructions, but does not specify which manufacturer's instructions to follow, which could lead to inconsistencies.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 237,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified TCNA methods',
        'full_text':
          'The document specifies that the tiling should conform to TCNA methods, but does not specify which methods apply, which could lead to confusion.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 236,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 2,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified ASTM standards',
        'full_text':
          'The document specifies that the gypsum board should conform to ASTM standards, but does not specify which ASTM standards apply, which could lead to confusion.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 235,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 2,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified ASTM standards',
        'full_text':
          'The document specifies that the backing boards and underlayments should conform to ASTM standards, but does not specify which ASTM standards apply, which could lead to confusion.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 234,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 2,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified ASTM standards',
        'full_text':
          'The document specifies that the cement plastering should conform to ASTM standards, but does not specify which ASTM standards apply, which could lead to confusion.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 233,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 2,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified ASTM standards',
        'full_text':
          'The document specifies that the metal lath and accessories should conform to ASTM standards, but does not specify which ASTM standards apply, which could lead to confusion.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 232,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified manufacturer instructions',
        'full_text':
          "The document specifies that the gypsum ceiling suspension systems should be installed according to the manufacturer's instructions, but does not specify which manufacturer's instructions to follow, which could lead to inconsistencies.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 231,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 2,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified ASTM standards',
        'full_text':
          'The document specifies that the non-structural metal framing should conform to ASTM standards, but does not specify which ASTM standards apply, which could lead to confusion.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 230,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified manufacturer instructions',
        'full_text':
          "The document specifies that the gypsum board shaft wall assemblies should be installed according to the manufacturer's instructions, but does not specify which manufacturer's instructions to follow, which could lead to inconsistencies.",
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 229,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 4,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Louver water-shedding details',
        'full_text':
          'The document specifies that the exterior metal wall louvers should be installed to shed water to the exterior of the building, but does not provide specific installation details or methods to ensure this is achieved.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 228,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 1,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Unspecified mirror distortion tolerances',
        'full_text':
          'The document specifies that the mirror glass should be installed plumb and level, free of optical distortion, but does not provide specific tolerances for what constitutes acceptable distortion.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 227,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Incomplete LEED submittal guidance',
        'full_text':
          'The glazing section requires submittals for LEED credits, but it does not specify the exact documentation needed for each credit, which could lead to incomplete submissions.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 226,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 3,
        'discipline_id': 4,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Installation instructions lacking',
        'full_text':
          'The specification for fluid-applied membrane air barriers does not include detailed instructions for the installation process, which could lead to improper installation and potential air leakage issues.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf': null,
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 225,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 4,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Installation instructions lacking',
        'full_text':
          'The specification for below-slab vapor retarders does not include detailed instructions for the installation process, which could lead to improper installation and potential moisture issues.',
        'assignee': {
          'id': 1,
          'full_name': 'John Doe',
          'role': 'admin',
        },
        'creator': null,
        'is_favourite': true,
        'xfdf':
          '<?xml version="1.0" encoding="UTF-8" ?> <xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"> <fields /> <add><highlight page="689" rect="144.023,367.885,540.147,453.877" color="#FFCD45" flags="print" name="225" title="Guest" subject="Highlight" date="D:20250305182952+05\'30\'" creationdate="D:20250305182952+05\'30\'" coords="172.82543900000002,453.87657899999994,375.390671,453.87657899999994,172.82543900000002,443.82845399999997,375.390671,443.82845399999997,144.02207900000002,441.15849999999995,152.30207900000002,441.15849999999995,144.02207900000002,431.110375,152.30207900000002,431.110375,172.82543900000002,441.15849999999995,540.1174070000001,441.15849999999995,172.82543900000002,431.110375,540.1174070000001,431.110375,172.82543900000005,428.56185999999997,540.0798709999999,428.56185999999997,172.82543900000005,418.51373499999994,540.0798709999999,418.51373499999994,172.82543900000005,415.84378,540.1461109999999,415.84378,172.82543900000005,405.79565499999995,540.1461109999999,405.79565499999995,172.82543900000005,403.24713999999994,441.9861590000003,403.24713999999994,172.82543900000005,393.199015,441.9861590000003,393.199015,144.02207900000002,390.650505,151.70591900000002,390.650505,144.02207900000002,380.60238000000004,151.70591900000002,380.60238000000004,172.82543900000002,390.650505,540.0268790000002,390.650505,172.82543900000002,380.60238000000004,540.0268790000002,380.60238000000004,172.825439,377.932425,303.0256790000001,377.932425,172.825439,367.8843,303.0256790000001,367.8843"><trn-custom-data bytes="{&quot;trn-annot-preview&quot;:&quot;Standard ampere ratings and number of poles.\\nb.\\nMechanical cover interlock with a manual\\ninterlock override, to prevent the\\nopening of the cover when the switch is in the on position. The interlock shall\\nprevent the switch from being turned on with the cover open. The operating handle\\nshall have lock-off means with provisions for three padlocks.\\nc. Auxiliary Contacts: One normally open and normally closed contact(s) that operate\\nwith switch handle operation.&quot;}"/></highlight></add> <modify /> <delete /> </xfdf>',
        'page_number': 689,
        'issue_type': 'drawing',
        'drawing_sheet_id': 25,
        'drawing_sheet': {
          'id': 25,
          'sheet_number': 'E-301',
          'title': 'Electrical Details',
        },
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 224,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 4,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Installation instructions lacking',
        'full_text':
          'The specification for above-grade vapor retarders does not include detailed instructions for the installation process, which could lead to improper installation and potential moisture issues.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf':
          '<?xml version="1.0" encoding="UTF-8" ?> <xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"> <fields /> <add><highlight page="366" rect="72,317.14,538.613,613.749" color="#FFCD45" flags="print" name="224" title="Guest" subject="Highlight" date="D:20250305182832+05\'30\'" creationdate="D:20250305182832+05\'30\'" coords="104.39896859999999,613.74805,208.21931353117967,613.74805,104.39896859999999,600.819604,208.21931353117967,600.819604,79.19999279999999,595.98805,88.60525867932012,595.98805,79.19999279999999,583.059604,88.60525867932012,583.059604,104.39896859999999,595.98805,216.44466998904173,595.98805,104.39896859999999,583.059604,216.44466998904173,583.059604,104.39896859999999,584.22805,348.3907574987862,584.22805,104.39896859999999,571.299604,348.3907574987862,571.299604,104.39896859999999,572.4670500000001,374.16975427036317,572.4670500000001,104.39896859999999,559.5386040000001,374.16975427036317,559.5386040000001,79.1998929,554.7070500000001,88.12540337900607,554.7070500000001,79.1998929,541.7786040000001,88.12540337900607,541.7786040000001,104.3988687,554.7070500000001,195.5729203079252,554.7070500000001,104.3988687,541.7786040000001,195.5729203079252,541.7786040000001,104.3988687,542.9470500000001,492.6861602338189,542.9470500000001,104.3988687,530.0186040000001,492.6861602338189,530.0186040000001,125.99984609999998,531.1870500000001,307.65450486061417,531.1870500000001,125.99984609999998,518.2586040000001,307.65450486061417,518.2586040000001,71.9998002,507.4280500000001,164.39800246781925,507.4280500000001,71.9998002,494.4996040000001,164.39800246781925,494.4996040000001,71.9998002,483.66705000000013,84.32993825918028,483.66705000000013,71.9998002,470.7386040000001,84.32993825918028,470.7386040000001,104.3987688,483.66705000000013,240.9534904163606,483.66705000000013,104.3987688,470.7386040000001,240.9534904163606,470.7386040000001,79.19979299999999,465.9080500000001,88.60505887932011,465.9080500000001,79.19979299999999,452.9796040000001,88.60505887932011,452.9796040000001,104.3987688,465.9080500000001,496.90809421844403,465.9080500000001,104.3987688,452.9796040000001,496.90809421844403,452.9796040000001,104.3987688,454.1480500000001,151.91178352455012,454.1480500000001,104.3987688,441.2196040000001,151.91178352455012,441.2196040000001,79.19979299999999,436.38805000000013,88.12530347900606,436.38805000000013,79.19979299999999,423.4596040000001,88.12530347900606,423.4596040000001,104.3987688,436.38805000000013,491.75084606055276,436.38805000000013,104.3987688,423.4596040000001,491.75084606055276,423.4596040000001,71.9998002,412.62805000000014,84.32993825918028,412.62805000000014,71.9998002,399.69960400000014,84.32993825918028,399.69960400000014,104.3987688,412.62805000000014,209.26098198547416,412.62805000000014,104.3987688,399.69960400000014,209.26098198547416,399.69960400000014,79.19979299999999,394.86805000000015,88.60505887932011,394.86805000000015,79.19979299999999,381.93960400000014,88.60505887932011,381.93960400000014,104.3987688,394.86805000000015,297.8967845691035,394.86805000000015,104.3987688,381.93960400000014,297.8967845691035,381.93960400000014,104.3987688,383.10705000000013,111.88633125998248,383.10705000000013,104.3987688,370.1786040000001,111.88633125998248,370.1786040000001,125.99974619999999,383.10705000000013,493.19344769882844,383.10705000000013,125.99974619999999,370.1786040000001,493.19344769882844,370.1786040000001,104.3987688,371.34705000000014,111.88633125998248,371.34705000000014,104.3987688,358.41860400000013,111.88633125998248,358.41860400000013,125.99974619999999,371.34705000000014,538.6129891171837,371.34705000000014,125.99974619999999,358.41860400000013,538.6129891171837,358.41860400000013,125.99974619999999,359.5880500000001,190.07439057269417,359.5880500000001,125.99974619999999,346.6596040000001,190.07439057269417,346.6596040000001,79.19969309999999,341.82805000000013,88.12520357900607,341.82805000000013,79.19969309999999,328.8996040000001,88.12520357900607,328.8996040000001,104.39866889999999,341.82805000000013,517.0637097856455,341.82805000000013,104.39866889999999,328.8996040000001,517.0637097856455,328.8996040000001,104.39866889999999,330.06805000000014,158.56073182042036,330.06805000000014,104.39866889999999,317.13960400000013,158.56073182042036,317.13960400000013"><trn-custom-data bytes="{&quot;trn-annot-preview&quot;:&quot;QUALITY ASSURANCE\\nA. Manufacturer Qualifications:\\n1. Company with minimum 5 years documented experience.\\n2. Able to supply complete and tested systems conforming to code.\\nB.\\nInstaller Qualifications:\\n1. Company specializing in work of this section with minimum 5 years documented experience in\\ncommercial quality work of comparable scope.\\nPART 2 - PRODUCTS\\n2.1 MANUFACTURERS, GENERAL\\nA. Where Manufacturers/Products are Specified: Provide specified manufacturers/products, or approved\\nsubstitution.\\nB. Approved Substitutions: Architect-approved equal products submitted as specified for substitutions.\\n2.2\\nSYSTEM DESCRIPTION\\nA. Systems Include: Backer for following conditions:\\n1. Backing board underlayment for medium mortar set stone veneer over metal stud wall framing.\\n2. Backing board underlayment for interior ceramic tile installations as part of TCNA method specified under\\nSection 093013.\\nB.\\nConform to Tile Council of America (TCNA) Methods and provisions of Section 093013 for backer board\\ntypes and use.&quot;}"/></highlight></add> <modify /> <delete /> </xfdf>',
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 223,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 1,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Installation instructions missing',
        'full_text':
          'The specification for batt and blanket insulation does not include detailed instructions for the installation process, which could lead to improper installation and reduced thermal and acoustical performance.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf':
          '<?xml version="1.0" encoding="UTF-8" ?> <xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"> <fields /> <add><highlight page="22" rect="72,423.46,536.543,613.749" color="#FFCD45" flags="print" name="223" title="Guest" subject="Highlight" date="D:20250305182756+05\'30\'" creationdate="D:20250305182756+05\'30\'" coords="104.39896859999999,613.74805,327.77049248953165,613.74805,104.39896859999999,600.819604,327.77049248953165,600.819604,79.19999279999999,595.98805,88.12550327900607,595.98805,79.19999279999999,583.059604,88.12550327900607,583.059604,104.39896859999999,595.98805,447.2893012074864,595.98805,104.39896859999999,583.059604,447.2893012074864,583.059604,79.19999279999999,578.22805,88.12550327900607,578.22805,79.19999279999999,565.299604,88.12550327900607,565.299604,104.39896859999999,578.22805,149.81817100031262,578.22805,104.39896859999999,565.299604,149.81817100031262,565.299604,104.39896859999999,566.4670500000001,376.4443060871177,566.4670500000001,104.39896859999999,553.5386040000001,376.4443060871177,553.5386040000001,104.39896859999999,554.7070500000001,111.8865310599825,554.7070500000001,104.39896859999999,541.7786040000001,111.8865310599825,541.7786040000001,125.999946,554.7070500000001,370.2795152517449,554.7070500000001,125.999946,541.7786040000001,370.2795152517449,541.7786040000001,71.99990009999999,530.9470500000001,84.33003815918028,530.9470500000001,71.99990009999999,518.0186040000001,84.33003815918028,518.0186040000001,104.3988687,530.9470500000001,237.747029042016,530.9470500000001,104.3988687,518.0186040000001,237.747029042016,518.0186040000001,79.1998929,513.1880500000001,343.41389656604207,513.1880500000001,79.1998929,500.2596040000001,343.41389656604207,500.2596040000001,79.1998929,495.4280500000001,88.12540337900606,495.4280500000001,79.1998929,482.4996040000001,88.12540337900606,482.4996040000001,104.3988687,495.4280500000001,536.5424833770123,495.4280500000001,104.3988687,482.4996040000001,536.5424833770123,482.4996040000001,104.3988687,483.66705000000013,523.7570361097253,483.66705000000013,104.3988687,470.7386040000001,523.7570361097253,470.7386040000001,104.3988687,471.9080500000001,255.68936750113812,471.9080500000001,104.3988687,458.9796040000001,255.68936750113812,458.9796040000001,79.1998929,454.1480500000001,88.12540337900606,454.1480500000001,79.1998929,441.2196040000001,88.12540337900606,441.2196040000001,104.3988687,454.1480500000001,304.0128078478593,454.1480500000001,104.3988687,441.2196040000001,304.0128078478593,441.2196040000001,79.1998929,436.38805000000013,88.8444656329201,436.38805000000013,79.1998929,423.4596040000001,88.8444656329201,423.4596040000001,104.3988687,436.38805000000013,331.1989169532615,436.38805000000013,104.3988687,423.4596040000001,331.1989169532615,423.4596040000001"><trn-custom-data bytes="{&quot;trn-annot-preview&quot;:&quot;Provide photographs at locations designated by Architect.\\nB. Photographer: Experienced in construction photography. May be Contractor\'s personnel.\\nC. Equipment:\\n1. As necessary to photograph both interior and exterior exposures.\\n2. Utilize full range of lenses, including wide angle and telephoto.\\n1.3\\nSUBMITTAL REQUIREMENTS\\nA. Make Submittals with each monthly Application for Payment.\\nB.\\nIdentify in metadata of each photograph the location where photograph was taken. Date stamp using month/day/\\nyear format. Create and use key plan as necessary to fully document the location of each photo provided with\\neach monthly Application for Payment.\\nC. Submit digital files in jpg format for each exposure.\\nD. Submit archival digital media for submittals of photograph&quot;}"/></highlight></add> <modify /> <delete /> </xfdf>',
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 222,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 1,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Installation process details missing',
        'full_text':
          'The specification for foam board insulation does not include detailed instructions for the installation process, which could lead to improper installation and reduced thermal performance.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf':
          '<?xml version="1.0" encoding="UTF-8" ?> <xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"> <fields /> <add><highlight page="18" rect="72,347.14,538.497,460.629" color="#FFCD45" flags="print" name="222" title="Guest" subject="Highlight" date="D:20250305182729+05\'30\'" creationdate="D:20250305182729+05\'30\'" coords="104.39866889999999,460.6281500000001,315.07209352092025,460.6281500000001,104.39866889999999,447.6997040000001,315.07209352092025,447.6997040000001,79.19969309999999,442.8681500000001,88.12520357900605,442.8681500000001,79.19969309999999,429.9397040000001,88.12520357900605,429.9397040000001,104.39866889999999,442.8681500000001,499.00102989251945,442.8681500000001,104.39866889999999,429.9397040000001,499.00102989251945,429.9397040000001,71.99970029999999,419.1071500000001,144.86332302720348,419.1071500000001,71.99970029999999,406.1787040000001,144.86332302720348,406.1787040000001,79.19969309999999,401.3471500000001,88.6049589793201,401.3471500000001,79.19969309999999,388.4187040000001,88.6049589793201,388.4187040000001,104.39866889999999,401.3471500000001,520.2069833712202,401.3471500000001,104.39866889999999,388.4187040000001,520.2069833712202,388.4187040000001,104.39866889999999,389.5881500000001,489.4976272638732,389.5881500000001,104.39866889999999,376.6597040000001,489.4976272638732,376.6597040000001,79.19969309999999,371.8281500000001,88.12520357900605,371.8281500000001,79.19969309999999,358.8997040000001,88.12520357900605,358.8997040000001,104.39866889999999,371.8281500000001,538.4968871160634,371.8281500000001,104.39866889999999,358.8997040000001,538.4968871160634,358.8997040000001,104.39866889999999,360.0681500000001,185.80829991138648,360.0681500000001,104.39866889999999,347.1397040000001,185.80829991138648,347.1397040000001"><trn-custom-data bytes="{&quot;trn-annot-preview&quot;:&quot;Submit Substitution Requests to Owner and Architect.\\nB. Submitter will be notified by Architect in writing of decision to accept or reject Substitution Requests.\\n1.3\\nFORMAT\\nA. Submit Substitution Request with completed Substitution Request Form attached to this Section. Electronic\\nversion available upon request. Other formats will not be reviewed, except at Architect\'s discretion.\\nB. Submit only one substitution on each Substitution Request Form. Multiple substitution requests on a single form\\nwill not be reviewed.&quot;}"/></highlight></add> <modify /> <delete /> </xfdf>',
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 221,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 1,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Application process details missing',
        'full_text':
          'The specification for pedestrian traffic coatings does not include detailed instructions for the application process, which could lead to improper application and potential safety hazards.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf':
          '<?xml version="1.0" encoding="UTF-8" ?> <xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"> <fields /> <add><highlight page="16" rect="72.001,488.98,537.629,685.028" color="#FFCD45" flags="print" name="221" title="Guest" subject="Highlight" date="D:20250305182659+05\'30\'" creationdate="D:20250305182659+05\'30\'" coords="104.3989728,685.02795,283.5610253330831,685.02795,104.3989728,672.099504,283.5610253330831,672.099504,79.199997,667.26795,88.60526287932012,667.26795,79.199997,654.339504,88.60526287932012,654.339504,104.3989728,667.26795,534.7656124413816,667.26795,104.3989728,654.339504,534.7656124413816,654.339504,104.3989728,655.50795,161.29537577189177,655.50795,104.3989728,642.579504,161.29537577189177,642.579504,79.199997,637.74805,88.12550747900607,637.74805,79.199997,624.819604,88.12550747900607,624.819604,104.3989728,637.74805,499.3053532077804,637.74805,104.3989728,624.819604,499.3053532077804,624.819604,104.3989728,625.98805,315.78594265474703,625.98805,104.3989728,613.059604,315.78594265474703,613.059604,79.199997,608.2270500000001,88.12550747900607,608.2270500000001,79.199997,595.2986040000001,88.12550747900607,595.2986040000001,104.3989728,608.2270500000001,535.5449830137387,608.2270500000001,104.3989728,595.2986040000001,535.5449830137387,595.2986040000001,104.3989728,596.4671500000001,535.9552117961869,596.4671500000001,104.3989728,583.538704,535.9552117961869,583.538704,104.3989728,584.7071500000001,140.11279459589275,584.7071500000001,104.3989728,571.7787040000001,140.11279459589275,571.7787040000001,72.0000042,560.9471500000001,281.8278165296402,560.9471500000001,72.0000042,548.0187040000001,281.8278165296402,548.0187040000001,79.199997,543.1871500000001,88.60526287932012,543.1871500000001,79.199997,530.2587040000001,88.60526287932012,530.2587040000001,104.3989728,543.1871500000001,427.81908442242224,543.1871500000001,104.3989728,530.2587040000001,427.81908442242224,530.2587040000001,79.199997,525.4271500000001,88.12550747900607,525.4271500000001,79.199997,512.4987040000001,88.12550747900607,512.4987040000001,104.3989728,525.4271500000001,537.6287992912789,525.4271500000001,104.3989728,512.4987040000001,537.6287992912789,512.4987040000001,104.3989728,513.6671500000001,536.1134035975352,513.6671500000001,104.3989728,500.7387040000001,536.1134035975352,500.7387040000001,104.3989728,501.9081500000001,425.8722254514209,501.9081500000001,104.3989728,488.9797040000001,425.8722254514209,488.9797040000001"><trn-custom-data bytes="{&quot;trn-annot-preview&quot;:&quot;OBJECTIONS TO SPECIFIED PRODUCTS\\nA. Submit Bids for only those products and systems specified by Project Manual, shown on Drawings, or accepted\\nby Addendum.\\nB.\\nWhere there are reasonable objections to specified products or systems and valid reason for proposing\\nsubstitution, conform to provisions of Section 012500.\\nC. Substitutions to specified products, not accepted by Architect, constitute non-conforming work and may require\\nremoval, repair, replacement, or monetary reimbursement in conformance to provisions of the Conditions of the\\nContract.\\n1.13 SAFETY DATA SHEETS (SDS OR MSDS)\\nA.\\nPost Safety Data Sheets for hazardous materials on site. Do not submit to Architect.\\nB. Provide bulletin board for hazard communication program, in location accessible 24 hours a day and convenient\\nto employees, subcontractors and their employees, and representatives for Owner, Architect, and other agencies\\nthat may visit project site and come in contact with hazardous chemical substances.&quot;}"/></highlight></add> <modify /> <delete /> </xfdf>',
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 220,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 3,
        'discipline_id': 4,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Installation instructions lacking',
        'full_text':
          'The specification for self-adhering sheet waterproofing does not include detailed instructions for the installation of the waterproofing system, which could lead to improper installation and potential water infiltration issues.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf':
          '<?xml version="1.0" encoding="UTF-8" ?> <xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"> <fields /> <add><highlight page="14" rect="104.399,269.619,431.274,282.548" color="#FFCD45" flags="print" name="220" title="Guest" subject="Highlight" date="D:20250305182631+05\'30\'" creationdate="D:20250305182631+05\'30\'" coords="104.39896859999999,282.5470500000001,431.2739153775934,282.5470500000001,104.39896859999999,269.6186040000001,431.2739153775934,269.6186040000001"><trn-custom-data bytes="{&quot;trn-annot-preview&quot;:&quot;Obtain and pay for the use of additional storage or work areas needed for operations.&quot;}"/></highlight></add> <modify /> <delete /> </xfdf>',
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      {
        'id': 219,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 1,
        'status_id': 1,
        'creator_id': null,
        'assignee_id': null,
        'summary': 'Adhesive type unspecified',
        'full_text':
          'The document does not specify the type of adhesive to be used for the installation of gypsum sheathing, which could lead to inconsistencies in the installation process and potential failures in the sheathing system.',
        'assignee': null,
        'creator': null,
        'is_favourite': false,
        'xfdf':
          '<?xml version="1.0" encoding="UTF-8" ?> <xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"> <fields /> <add><highlight page="10" rect="36.001,166.881,563.736,222.062" color="#FFCD45" flags="print" name="219" title="Guest" subject="Highlight" date="D:20250305182424+05\'30\'" creationdate="D:20250305182424+05\'30\'" coords="36.00000000000003,222.061827,488.545860076345,222.061827,36.00000000000003,210.07946100000004,488.545860076345,210.07946100000004,36.00000000000003,205.50282700000002,539.9388674977313,205.50282700000002,36.00000000000003,193.52046100000007,539.9388674977313,193.52046100000007,36.00000000000003,189.18282699999997,563.7354871756735,189.18282699999997,36.00000000000003,177.20046100000002,563.7354871756735,177.20046100000002,36.00000000000003,178.86282700000004,524.4457788469394,178.86282700000004,36.00000000000003,166.88046099999997,524.4457788469394,166.88046099999997"><trn-custom-data bytes="{&quot;trn-annot-preview&quot;:&quot;A fillable version of this document is available upon request. Submit electronically via approved project management system.\\nSubmit all items in accordance with Section 013300 - Submittal Procedures and the individual specification sections. Do not submit MSDS.\\n*Use separate submittal forms for informational submittals and action submittals – DO NOT COMBINE. However, to the greatest extent possible,\\nsubmit all items of a specification section at the same time, per requirements for concurrent submittals under \\&quot;Coordination\\&quot; in 013300.&quot;}"/></highlight></add> <modify /> <delete /> </xfdf>',
        'comments': [],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      // spec
      {
        'id': 218,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 1,
        'status_id': 1,
        'creator_id': 1,
        'creator': {
          'id': 1,
          'full_name': 'Gaurav Singh',
          'role': 'admin',
        },
        'assignee_id': 2,
        'assignee': {
          'id': 2,
          'full_name': 'John Smith',
          'role': 'engineer',
        },
        'issue_type': 'spec',
        'summary': 'Missing treatment instructions',
        'full_text':
          'The specification for the installation of rough carpentry does not include specific instructions for the treatment of cut surfaces of preservative-treated lumber, which is critical for ensuring the longevity and safety of the wood used in construction.',
        'is_favourite': false,
        'csi_code': '060573',
        'xfdf': `<?xml version="1.0" encoding="UTF-8" ?>
<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">
<fields />
<add><square page="0" rect="142.91,489.41,420.76,566.05" color="#E44234" flags="print" name="218" title="Guest" subject="Rectangle" date="D:20250312183524+05'30'" creationdate="D:20250312183522+05'30'" dashes=""/></add>
<modify />
<delete />
</xfdf>
`,
        'comments': [],
        'evaluation': {
          'id': 11,
          'issue_id': 6684,
          'evaluator_id': 1,
          'is_approved': false,
          'explanation': 'good 6684',
          'created_at': '2025-06-23T12:49:08.205644Z',
          'updated_at': '2025-06-23T12:49:08.205644Z',
          'evaluator': {
            'id': 1,
            'full_name': 'Dev User',
            'role': 'Architect',
          },
        },
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
      // drawing
      {
        'id': 217,
        'document_id': 116,
        'project_id': 67,
        'priority_id': 2,
        'discipline_id': 6,
        'status_id': 1,
        'creator_id': 1,
        'creator': {
          'id': 1,
          'full_name': 'Gaurav Singh',
          'role': 'admin',
        },
        'assignee_id': 2,
        'assignee': {
          'id': 2,
          'full_name': 'John Smith',
          'role': 'engineer',
        },
        'issue_type': 'drawing',
        'summary': 'Incorrect CSI code',
        'full_text':
          "The document specifies 'Preservative Wood Treatment' under Division 06 - Wood and Plastic with the CSI code 060573.33. However, the correct CSI code for preservative wood treatment is 060573. This discrepancy could cause issues in locating the correct section during construction.",
        'is_favourite': false,
        'xfdf': `<?xml version="1.0" encoding="UTF-8" ?>
<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">
<fields />
<add><square page="0" rect="146.91,681.02,298.6,703.38" color="#E44234" flags="print" name="217" title="Guest" subject="Rectangle" date="D:20250312182249+05'30'" creationdate="D:20250312182248+05'30'" dashes=""/></add>
<modify />
<delete />
</xfdf>
`,
        'page_number': 12,
        'issue_type': 'spec',
        'csi_code': '060573',
        'comments': [
          {
            'id': 3,
            'issue_id': 217,
            'author_id': 1,
            'text': 'this is first comment',
            'created_at': '2025-03-05T13:08:59.469964Z',
            'updated_at': '2025-03-05T13:08:59.469964Z',
            'author': {
              'id': 1,
              'full_name': 'Gaurav Singh',
              'role': 'Admin',
            },
          },
          {
            'id': 4,
            'issue_id': 217,
            'author_id': 1,
            'text': 'this is second comment',
            'created_at': '2025-03-05T13:09:03.619547Z',
            'updated_at': '2025-03-05T13:09:03.619547Z',
            'author': {
              'id': 1,
              'full_name': 'Gaurav Singh',
              'role': 'Admin',
            },
          },
          {
            'id': 5,
            'issue_id': 217,
            'author_id': 1,
            'text': 'this is third comment. @[Michael Chen](4) hey\n',
            'created_at': '2025-03-05T13:09:07.962916Z',
            'updated_at': '2025-03-05T13:09:07.962916Z',
            'author': {
              'id': 1,
              'full_name': 'Gaurav Singh',
              'role': 'Admin',
            },
          },
        ],
        'created_at': '2025-03-05T12:43:49.865651Z',
        'updated_at': '2025-03-05T12:43:49.865651Z',
      },
    ],
  },
  drawingOverlay: {
    editingDrawingOverlayId: null,
  },
};
